import React, { useState, useMemo } from 'react';
import { BarChart3, Users, TrendingUp, Download, Calendar, Eye, UserCheck, Smartphone, Globe, MapPin, Briefcase, Activity, QrCode as QrCodeIcon, MousePointer, Share2, Target, DollarSign, Home as HomeIcon, X } from 'lucide-react';
import { User, SystemStats, Lead } from '../types';
import { countries } from '../data/mockData';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
const QrCode = QrCodeIcon;
// Optional: default homepage settings for plan names fallback
import { defaultHomepageSettings } from '../data/mockData';
import { generateRecommendations } from '../utils/recommendations';

// --- Stable seeded PRNG (prevents flickering stats) ---
function hashStringToSeed(str: string): number {
  let h = 1779033703 ^ str.length;
  for (let i = 0; i < str.length; i++) {
    h = Math.imul(h ^ str.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  return (h >>> 0) || 1;
}

function mulberry32(a: number) {
  return function() {
    let t = (a += 0x6D2B79F5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}


interface AdvancedAnalyticsPanelProps {
  users: User[];
  systemStats: SystemStats;
  leads?: Lead[];
  showRevenue?: boolean;
}

type TimeFilter = '24h' | '3d' | '7d' | '1m' | '3m' | '6m' | '12m';
type LocationMode = 'country' | 'city';


// Stable random per time filter and key totals
interface AnalyticsData {
  mostActiveUsers: Array<{ 
    user: User; 
    activity: number;
    views: number;
    clicks: number;
    shares: number;
    saves: number;
    qrScans: number;
  }>;
  genderDistribution: { [key: string]: number };
  ageDistribution: { [key: string]: number };
  positionDistribution: { [key: string]: number };
  locationDistribution: { [key: string]: number };
  deviceDistribution: { [key: string]: number };
  socialMediaDistribution: { [key: string]: number };
  trafficSourceDistribution: { [key: string]: number };
  searchEngineDistribution: { [key: string]: number };
  referralSourceDistribution: { [key: string]: number };
  newUsersOverTime: Array<{ date: string; count: number }>;
  userEngagement: {
    totalViews: number;
    uniqueVisitors: number;
    returningVisitors: number;
    averageSessionTime: number;
    totalClicks: number;
    totalShares: number;
    totalSaves: number;
    totalQRScans: number;
  };
  subscriptionTrends: { [key: string]: number };
  newUsersStats: {
    totalNewUsers: number;
    newUsersToday: number;
    growthRate: number;
    dailyNewUsers: Array<{ date: string; count: number }>;
  };
  // Optional additional breakdowns used by some report sections
  trafficSources?: { [key: string]: number };
  geographicDistribution?: { [key: string]: number };
}

export const AdvancedAnalyticsPanel: React.FC<AdvancedAnalyticsPanelProps> = ({
  users,
  systemStats,
  showRevenue = true
}) => {
  // Safe number coercion to avoid NaN/undefined issues throughout analytics
  const num = (v: any): number => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };
  const [timeFilter, setTimeFilter] = useState<TimeFilter>('1m');
  const seededRand = useMemo(() => {
    const seedStr = [
      timeFilter,
      users.length,
      num(systemStats.totalUsers),
      num(systemStats.activeUsers),
      num(systemStats.totalViews),
      num(systemStats.totalShares)
    ].join('|');
    return mulberry32(hashStringToSeed(seedStr));
  }, [timeFilter, users.length, systemStats.totalUsers, systemStats.activeUsers, systemStats.totalViews, systemStats.totalShares]);
  const [activeChart, setActiveChart] = useState<'overview' | 'demographics' | 'engagement' | 'trends'>('overview');
  const [locationMode, setLocationMode] = useState<LocationMode>('country');
  const [exportLocationMode, setExportLocationMode] = useState<LocationMode>('country');
  const [showHomeStats, setShowHomeStats] = useState(false);

  // Server-provided engagement totals (optional) with safe coercion
  const totals = useMemo(() => ({
    viewsServer: num((systemStats as any)?.totalViews),
    sharesServer: num((systemStats as any)?.totalShares),
    adViewsServer: num((systemStats as any)?.totalAdViews),
    adClicksServer: num((systemStats as any)?.totalAdClicks),
    adRedirectsServer: num((systemStats as any)?.totalAdRedirects),
    cardClicksServer: num((systemStats as any)?.totalCardClicks),
    emailClicksServer: num((systemStats as any)?.totalEmailClicks),
    shareClicksServer: num((systemStats as any)?.totalShareClicks),
    callClicksServer: num((systemStats as any)?.totalCallClicks),
    savesServer: num((systemStats as any)?.totalCardSaveClicks),
    socialClicksServer: num((systemStats as any)?.totalSocialClicks),
    qrScansServer: num((systemStats as any)?.totalQrScans),
  }), [systemStats]);

  // Fallback aggregate from users when server totals are not present
  const aggregateFromUsers = (key: keyof NonNullable<User['analytics']>) =>
    users.reduce((sum, u) => sum + num((u as any)?.analytics?.[key]), 0);

  // Helper: build traffic breakdowns consistently from total views, with fallback baseline when total is 0
  const allowMock = Boolean((systemStats as any)?.analyticsFlags?.enableMockStats);
  const buildTrafficBreakdown = (total: number, entries: Array<{ label: string; weight: number }>) => {
    const safeTotal = Math.max(0, num(total));
    // Real-only: do not fabricate when real totals are missing
    if (safeTotal === 0 || !allowMock) return entries.map(e => ({ label: e.label, visits: 0, percentage: '0.0%' }));

    return entries.map(e => {
      const visits = Math.floor(safeTotal * e.weight);
      const pct = safeTotal > 0 ? `${((visits / safeTotal) * 100).toFixed(1)}%` : '0.0%';
      return { label: e.label, visits, percentage: pct };
    });
  };

  // Helpers: prefer real data if available
  const isValidCountsObject = (obj: any): obj is Record<string, number> => {
    if (!obj || typeof obj !== 'object') return false;
    let sum = 0; let hasNum = false;
    for (const v of Object.values(obj)) {
      const n = Number(v);
      if (Number.isFinite(n) && n > 0) { sum += n; hasNum = true; }
    }
    return hasNum && sum > 0;
  };
  const objectToBreakdown = (obj: Record<string, number>) => {
    const entries = Object.entries(obj).map(([k, v]) => ({ label: k, visits: num(v) }));
    const total = entries.reduce((s, e) => s + e.visits, 0) || 0;
    return entries.map(e => ({ label: e.label, visits: e.visits, percentage: total > 0 ? `${((e.visits / total) * 100).toFixed(1)}%` : '0.0%' }));
  };
  // Version that calculates percentages against a specific total (for traffic sources)
  const objectToBreakdownWithTotal = (obj: Record<string, number>, totalViews: number) => {
    const entries = Object.entries(obj).map(([k, v]) => ({ label: k, visits: num(v) }));
    // Use provided totalViews when positive; otherwise fall back to sum of the entries
    const sumEntries = entries.reduce((s, e) => s + (Number.isFinite(e.visits) && e.visits > 0 ? e.visits : 0), 0);
    const baseTotal = num(totalViews) > 0 ? num(totalViews) : sumEntries;
    return entries.map(e => {
      const rawPct = baseTotal > 0 && e.visits > 0 ? (e.visits / baseTotal) * 100 : 0;
      // Floor non-zero values to 0.1% so they never show as 0%
      const flooredPct = e.visits > 0 && rawPct > 0 && rawPct < 0.1 ? 0.1 : rawPct;
      const clampedPct = Math.min(100, flooredPct);
      return {
        label: e.label,
        visits: e.visits,
        percentage: clampedPct, // numeric percent for meter width
        percentageLabel: `${clampedPct.toFixed(1)}%` // text label
      };
    });
  };
  const pick = (...paths: Array<any>) => paths.find(p => isValidCountsObject(p)) as Record<string, number> | undefined;

  // Location distribution with normalized data
  const locationDistribution = useMemo(() => {
    const locationCounts: { [key: string]: number } = {};
    // Prefer server-provided demographics when available
    const demo = (systemStats as any)?.demographics || {};
    const byCountry = demo?.locations as Record<string, number> | undefined;
    const byCity = demo?.locationsByCity as Record<string, number> | undefined;

    if (locationMode === 'city' && byCity && Object.keys(byCity).length > 0) {
      Object.assign(locationCounts, byCity);
    } else if (byCountry && Object.keys(byCountry).length > 0) {
      Object.assign(locationCounts, byCountry);
    } else {
      // Fallback: derive from users
      users.forEach(user => {
        const raw = String(user.location || '').trim();
        if (!raw) { locationCounts['Unknown'] = (locationCounts['Unknown'] || 0) + 1; return; }
        if (locationMode === 'city' && raw.includes(',')) {
          // Expect "City, Country"
          const parts = raw.split(',').map(s => s.trim()).filter(Boolean);
          const city = parts[0];
          const country = parts[parts.length - 1];
          const key = city && country ? `${city}, ${country}` : raw;
          locationCounts[key] = (locationCounts[key] || 0) + 1;
        } else {
          // Country-only
          const matchedCountry = countries.find(country => country.toLowerCase() === raw.toLowerCase());
          const normalized = matchedCountry || (raw.includes(',') ? raw.split(',').pop()!.trim() : raw);
          locationCounts[normalized] = (locationCounts[normalized] || 0) + 1;
        }
      });
    }

    return Object.entries(locationCounts)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 12);
  }, [users, systemStats, locationMode]);

  // Generate analytics data with real-or-mock fallbacks based on time filter
  const analyticsData = useMemo((): AnalyticsData => {
    // Real-only rendering: do not scale or fabricate values with time filter
    const multiplier = 1;

    // Most active users
    const mostActiveUsers = users
      .map(user => ({
        user,
        activity: Math.floor((num(user.analytics?.views) + num(user.analytics?.contactClicks) + num(user.analytics?.shares)) * multiplier),
        views: Math.floor(num(user.analytics?.views) * multiplier),
        clicks: Math.floor((num(user.analytics?.contactClicks) + num(user.analytics?.messageClicks) + num(user.analytics?.socialClicks)) * multiplier),
        shares: Math.floor(num(user.analytics?.shares) * multiplier),
        saves: Math.floor(num(user.analytics?.saves) * multiplier),
        qrScans: Math.floor(num(user.analytics?.qrScans) * multiplier)
      }))
      .sort((a, b) => b.activity - a.activity)
      .slice(0, 10);

    // Gender distribution (mock data)
    const genderDistribution = allowMock ? {
      'Male': Math.floor(users.length * 0.52 * multiplier),
      'Female': Math.floor(users.length * 0.45 * multiplier),
      'Other': Math.floor(users.length * 0.03 * multiplier)
    } : { } as any;

    // Age distribution (mock data)
    const ageDistribution = allowMock ? {
      '18-25': Math.floor(users.length * 0.15 * multiplier),
      '25-35': Math.floor(users.length * 0.35 * multiplier),
      '35-45': Math.floor(users.length * 0.30 * multiplier),
      '45-55': Math.floor(users.length * 0.15 * multiplier),
      '55+': Math.floor(users.length * 0.05 * multiplier)
    } : { } as any;

    // Position distribution
    const positionCounts: { [key: string]: number } = {};
    users.forEach(user => {
      const position = (user.position || 'Other').split(' ')[0]; // Get first word of position safely
      positionCounts[position] = (positionCounts[position] || 0) + 1;
    });

    // Location distribution (mock data based on common locations)
  const locationDistribution = allowMock ? {
      'United States': Math.floor(users.length * 0.40 * multiplier),
      'Canada': Math.floor(users.length * 0.15 * multiplier),
      'United Kingdom': Math.floor(users.length * 0.12 * multiplier),
      'Germany': Math.floor(users.length * 0.08 * multiplier),
      'Australia': Math.floor(users.length * 0.06 * multiplier),
      'Tanzania': Math.floor(users.length * 0.05 * multiplier),
      'Other': Math.floor(users.length * 0.14 * multiplier)
    } : { } as any;

    // Device distribution (mock data)
    const deviceDistribution = allowMock ? {
      'Mobile': Math.floor(users.length * 0.65 * multiplier),
      'Desktop': Math.floor(users.length * 0.25 * multiplier),
      'Tablet': Math.floor(users.length * 0.10 * multiplier)
    } : { } as any;

    // Social Media Distribution (mock data)
    const socialMediaDistribution = allowMock ? {
      'LinkedIn': Math.floor(users.length * 0.35 * multiplier),
      'Twitter': Math.floor(users.length * 0.25 * multiplier),
      'Facebook': Math.floor(users.length * 0.20 * multiplier),
      'Instagram': Math.floor(users.length * 0.15 * multiplier),
      'WhatsApp': Math.floor(users.length * 0.05 * multiplier)
    } : { } as any;

    // Traffic Source Distribution (mock data)
    const trafficSourceDistribution = allowMock ? {
      'Direct': Math.floor(users.length * 0.40 * multiplier),
      'Social Media': Math.floor(users.length * 0.30 * multiplier),
      'Search Engines': Math.floor(users.length * 0.20 * multiplier),
      'Email Marketing': Math.floor(users.length * 0.06 * multiplier),
      'Referrals': Math.floor(users.length * 0.04 * multiplier)
    } : { } as any;

    // Search Engine Distribution (mock data)
    const searchEngineDistribution = allowMock ? {
      'Google': Math.floor(users.length * 0.75 * multiplier),
      'Bing': Math.floor(users.length * 0.15 * multiplier),
      'Yahoo': Math.floor(users.length * 0.06 * multiplier),
      'DuckDuckGo': Math.floor(users.length * 0.03 * multiplier),
      'Baidu': Math.floor(users.length * 0.01 * multiplier)
    } : { } as any;

    // Referral Source Distribution (mock data)
    const referralSourceDistribution = allowMock ? {
      'Business Networks': Math.floor(users.length * 0.30 * multiplier),
      'Partner Websites': Math.floor(users.length * 0.25 * multiplier),
      'Industry Forums': Math.floor(users.length * 0.20 * multiplier),
      'Professional Blogs': Math.floor(users.length * 0.15 * multiplier),
      'Other': Math.floor(users.length * 0.10 * multiplier)
    } : { } as any;

    // New users over time (mock data)
    const newUsersOverTime = allowMock ? Array.from({ length: 7 }, (_, i) => ({
      date: new Date(Date.now() - (6 - i) * 24 * 60 * 60 * 1000).toLocaleDateString(),
      count: Math.floor(seededRand() * 20 * multiplier) + 5
    })) : [];

    // Establish a robust base for totalViews: prefer real, else derive from users, else conservative baseline
    const totalViewsBase = (() => {
      const real = num(systemStats.totalViews);
      if (real > 0) return real;
      const fromUsers = users.reduce((sum, user) => sum + num(user.analytics?.views), 0);
      if (fromUsers > 0) return fromUsers;
      // Real-only: no fabricated baseline
      return 0;
    })();

    // User engagement
    const userEngagement = {
      totalViews: Math.floor(totalViewsBase * multiplier),
      uniqueVisitors: Math.floor(num(systemStats.uniqueVisitors || systemStats.activeUsers) * multiplier),
      returningVisitors: Math.floor(num(systemStats.returningVisitors || (systemStats.activeUsers * 0.3)) * multiplier),
      averageSessionTime: Math.floor(num(systemStats.averageSessionTime) || Math.floor(180 + seededRand() * 120)), // prefer server weighted session time
      totalClicks: Math.floor(users.reduce((sum, user) => sum + num(user.analytics?.contactClicks) + num(user.analytics?.messageClicks) + num(user.analytics?.socialClicks), 0) * multiplier),
      totalShares: Math.floor(users.reduce((sum, user) => sum + num(user.analytics?.shares), 0) * multiplier),
      totalSaves: Math.floor(users.reduce((sum, user) => sum + num(user.analytics?.saves), 0) * multiplier),
      totalQRScans: Math.floor(users.reduce((sum, user) => sum + num(user.analytics?.qrScans), 0) * multiplier)
    };

    // Subscription trends
    const subscriptionTrends = {
      'Trial': Math.floor(num(systemStats.trialUsers) * multiplier),
      'Silver': Math.floor(num(systemStats.silverSubscriptions) * multiplier),
      'Gold': Math.floor(num(systemStats.goldSubscriptions) * multiplier)
    };

    // New Users Stats
  const baseNewUsers = num(systemStats.newUsersToday) || 0;
    const totalNewUsers = Math.floor(baseNewUsers * multiplier * (timeFilter === '24h' ? 1 : 
                                    timeFilter === '3d' ? 3 : 
                                    timeFilter === '7d' ? 7 : 
                                    timeFilter === '1m' ? 30 : 
                                    timeFilter === '3m' ? 90 : 
                                    timeFilter === '6m' ? 180 : 365));
    
    const newUsersToday = Math.floor(baseNewUsers * multiplier);
  const previousPeriodUsers = Math.floor(totalNewUsers * 0.85); // Mock previous period
  const growthRate = previousPeriodUsers > 0 ? ((totalNewUsers - previousPeriodUsers) / previousPeriodUsers) * 100 : 0;
    
    // Daily new users for the selected period
    const daysInPeriod = timeFilter === '24h' ? 1 : timeFilter === '3d' ? 3 : timeFilter === '7d' ? 7 : timeFilter === '1m' ? 30 : timeFilter === '3m' ? 90 : timeFilter === '6m' ? 180 : 365;
    const dailyNewUsers = allowMock ? Array.from({ length: Math.min(daysInPeriod, 30) }, (_, i) => {
      const date = new Date(Date.now() - (daysInPeriod - 1 - i) * 24 * 60 * 60 * 1000);
      return { date: date.toLocaleDateString(), count: Math.floor(seededRand() * (baseNewUsers * 2) * multiplier) + Math.floor(baseNewUsers * 0.5 * multiplier) };
    }) : [];

    // Enhanced social media distribution
    // Device usage distribution
    // Traffic source distribution
    const trafficSources = allowMock ? {
      direct: Math.floor(totalViewsBase * 0.35),
      social: Math.floor(totalViewsBase * 0.28),
      search: Math.floor(totalViewsBase * 0.22),
      referral: Math.floor(totalViewsBase * 0.15)
    } : {} as any;

    // Geographic distribution (enhanced)
    const geographicDistribution = allowMock ? {
      'North America': Math.floor(users.length * 0.35),
      'Europe': Math.floor(users.length * 0.28),
      'Asia': Math.floor(users.length * 0.22),
      'Africa': Math.floor(users.length * 0.08),
      'South America': Math.floor(users.length * 0.05),
      'Oceania': Math.floor(users.length * 0.02)
    } : {} as any;
    const newUsersStats = {
      totalNewUsers,
      newUsersToday,
      growthRate,
      dailyNewUsers
    };

    return {
      mostActiveUsers,
      socialMediaDistribution,
      deviceDistribution,
      trafficSources,
      geographicDistribution,
      genderDistribution,
      ageDistribution,
      positionDistribution: positionCounts,
  // Note: this locationDistribution is a mock used in other charts; the panel-specific list above prefers server data and mode
  // Keep mock locationDistribution for other charts; the Demographics section will render from the memoized distribution above
  locationDistribution,
      trafficSourceDistribution,
      searchEngineDistribution,
      referralSourceDistribution,
      newUsersOverTime,
      userEngagement,
      subscriptionTrends,
      newUsersStats
    };
  }, [users, systemStats, timeFilter, seededRand]);

  // Prefer real traffic counts if present in systemStats; else fall back to mock breakdowns per section
  const realSocialCounts = pick(
    (systemStats as any)?.traffic?.social,
    (systemStats as any)?.traffic?.socialMedia,
    (systemStats as any)?.socialTraffic,
    (systemStats as any)?.socialMedia
  );
  const realOtherCounts = pick(
    (systemStats as any)?.traffic?.other,
    (systemStats as any)?.otherTraffic,
    (systemStats as any)?.traffic?.sources
  );

  // Robust overall total views used for percentage calculations
  const overallTotalViews = useMemo(() => {
    const sysTotal = num((systemStats as any)?.totalViews);
    const engagementTotal = num((systemStats as any)?.userEngagement?.totalViews) || num((systemStats as any)?.homepage?.views);
    const panelTotal = num((analyticsData as any)?.userEngagement?.totalViews);
    const sumCounts = (counts?: Record<string, number>) => counts ? Object.values(counts).reduce((s, v) => s + num(v), 0) : 0;
    const otherSum = sumCounts(realOtherCounts);
    const socialSum = sumCounts(realSocialCounts);
    const derivedTotal = otherSum + socialSum;
    const base = sysTotal || engagementTotal || panelTotal || derivedTotal;
    return base > 0 ? base : 100; // sensible floor so meters render
  }, [systemStats, analyticsData, realOtherCounts, realSocialCounts]);

  // --- Home Stats derivation (prefer real server data if available) ---
  const homeStats = useMemo(() => {
  const _seeded = seededRand();
    const prefer = (v: any, fallback: number) => {
      const n = Number(v);
      return Number.isFinite(n) && n >= 0 ? n : Math.floor(fallback);
    };
    const home = (systemStats as any)?.homepage || (systemStats as any)?.home || {};
    const totalViewsBase = (() => {
      const real = Number(home?.views);
      if (Number.isFinite(real) && real > 0) return real;
      // Baseline: 20% of overall views if present, else derive from users count
      const fromSystem = Number((systemStats as any)?.totalViews) || 0;
      if (fromSystem > 0) return Math.max(300, Math.floor(fromSystem * 0.2));
      return Math.max(300, users.length * 60);
    })();
    // Device usage
    const deviceCountsReal = (home?.devices as Record<string, number>) || undefined;
    const deviceEntries = deviceCountsReal && Object.keys(deviceCountsReal).length > 0
      ? objectToBreakdown(deviceCountsReal)
      : buildTrafficBreakdown(totalViewsBase, [
          { label: 'Mobile', weight: 0.64 },
          { label: 'Desktop', weight: 0.28 },
          { label: 'Tablet', weight: 0.08 }
        ]);
    // Platform (OS) usage
    const platformCountsReal = (home?.platforms as Record<string, number>) || undefined;
    const platformEntries = platformCountsReal && Object.keys(platformCountsReal).length > 0
      ? objectToBreakdown(platformCountsReal)
      : buildTrafficBreakdown(totalViewsBase, [
          { label: 'Windows', weight: 0.42 },
          { label: 'Android', weight: 0.22 },
          { label: 'iOS', weight: 0.18 },
          { label: 'macOS', weight: 0.14 },
          { label: 'Linux', weight: 0.04 }
        ]);
    // Social platforms
    const socialCountsReal = pick(home?.traffic?.social, (systemStats as any)?.homepage?.socialTraffic, realSocialCounts);
    const socialEntries = socialCountsReal
      ? objectToBreakdown(socialCountsReal)
      : buildTrafficBreakdown(totalViewsBase * 0.28, [
          { label: 'LinkedIn', weight: 0.36 },
          { label: 'Twitter', weight: 0.22 },
          { label: 'Facebook', weight: 0.20 },
          { label: 'Instagram', weight: 0.17 },
          { label: 'WhatsApp', weight: 0.05 }
        ]);
    // Other platforms/sources
    const otherCountsReal = pick(home?.traffic?.other, (systemStats as any)?.homepage?.traffic?.other, realOtherCounts);
    const otherEntries = otherCountsReal
      ? objectToBreakdown(otherCountsReal)
      : buildTrafficBreakdown(totalViewsBase * 0.72, [
          { label: 'Direct', weight: 0.36 },
          { label: 'Search', weight: 0.32 },
          { label: 'Referral', weight: 0.18 },
          { label: 'Email', weight: 0.14 }
        ]);
    // Click metrics
    const contactClicks = prefer(home?.clicks?.contact, totalViewsBase * 0.03);
    const authClicksTotal = Math.max(5, Math.floor(totalViewsBase * 0.025));
    const ownerSignIn = prefer(home?.clicks?.ownerSignIn, Math.floor(authClicksTotal * 0.55));
    const ownerSignUp = prefer(home?.clicks?.ownerSignUp, Math.max(1, authClicksTotal - ownerSignIn));
    // Subscription plan clicks
    const planClicksReal = (home?.planClicks?.byPlan as Record<string, number>) || (systemStats as any)?.planClicks || undefined;
    const plans = (defaultHomepageSettings as any)?.pricing?.plans || [];
    const planNames: string[] = Array.isArray(plans) ? plans.map((p: any) => String(p?.name || 'Plan')) : ['Free Trial', 'Silver Plan', 'Gold Plan'];
    const perPlan = planClicksReal && Object.keys(planClicksReal).length > 0
      ? Object.entries(planClicksReal).map(([name, v]) => ({ label: name, visits: Number(v) }))
      : (() => {
          const total = Math.max(10, Math.floor(totalViewsBase * 0.06));
          // Weight popular plan more if present
          const weights = planNames.map((name: string) => (String(name).toLowerCase().includes('gold') ? 0.5 : String(name).toLowerCase().includes('silver') ? 0.3 : 0.2));
          const sumW = weights.reduce((s, w) => s + w, 0) || 1;
          return planNames.map((name: string, i: number) => ({ label: name, visits: Math.floor(total * (weights[i] / sumW)) }));
        })();
    const totalPlanClicks = prefer(home?.planClicks?.total, perPlan.reduce((s, e) => s + Number(e.visits || 0), 0));
    const toPercent = (entries: Array<{ label: string; visits: number }>) => {
      const total = entries.reduce((s, e) => s + (Number(e.visits) || 0), 0);
      return entries.map(e => ({ ...e, percentage: total > 0 ? `${((Number(e.visits) / total) * 100).toFixed(1)}%` : '0.0%' }));
    };
    return {
      totalViews: totalViewsBase,
      deviceEntries: toPercent(deviceEntries as any),
      platformEntries: toPercent(platformEntries as any),
      socialEntries: toPercent(socialEntries as any),
      otherEntries: toPercent(otherEntries as any),
      contactClicks,
      ownerSignIn,
      ownerSignUp,
      totalPlanClicks,
      perPlan: toPercent(perPlan as any),
    };
  }, [systemStats, users.length, timeFilter, realSocialCounts, realOtherCounts, buildTrafficBreakdown, objectToBreakdown, pick, seededRand]);

  // Generate strategic recommendations using the simple rules engine
  const recommendations = useMemo(() => {
    try {
      return generateRecommendations({ systemStats, analyticsData, homeStats });
    } catch (e) {
      return [] as Array<{ category: string; insight: string; recommendation: string; severity?: string }>;
    }
  }, [systemStats, analyticsData, homeStats]);

  // Prefer server totals in overview when available; otherwise fall back to derived per-user aggregates
  const overviewDerived = useMemo(() => {
    const serverClicksSum = Math.max(0,
      Number(totals.cardClicksServer || 0) + Number(totals.emailClicksServer || 0) + Number(totals.socialClicksServer || 0)
    );
    const totalClicksEffective = serverClicksSum > 0 ? serverClicksSum : Number(analyticsData.userEngagement.totalClicks || 0);
    const totalSharesEffective = Number(totals.sharesServer || 0) > 0 ? Number(totals.sharesServer) : Number(analyticsData.userEngagement.totalShares || 0);
    const adViewsEffective = Number(totals.adViewsServer || 0) > 0 ? Number(totals.adViewsServer) : Math.floor(Number(analyticsData.userEngagement.totalViews || 0) * 0.15);
    const adClicksEffective = Number(totals.adClicksServer || 0) > 0 ? Number(totals.adClicksServer) : Math.floor(totalClicksEffective * 0.08);
    const adRedirectsEffective = Number(totals.adRedirectsServer || 0) > 0 ? Number(totals.adRedirectsServer) : Math.max(0, Math.floor(adClicksEffective * 0.6));
    const showGuidance = (totalClicksEffective <= 0) && (totalSharesEffective <= 0) && (adViewsEffective <= 0) && (adClicksEffective <= 0) && (adRedirectsEffective <= 0);
    return { serverClicksSum, totalClicksEffective, totalSharesEffective, adViewsEffective, adClicksEffective, adRedirectsEffective, showGuidance };
  }, [totals, analyticsData]);

  const exportToPDF = () => {
    const doc = new jsPDF();
    let yPosition = 30;
  const pageHeight = doc.internal.pageSize.height;
  const pageWidth = doc.internal.pageSize.width;
    const margin = 20;
    let currentPage = 1;
    const totalPages = 8;

    // Helper function to add new page
    const addNewPage = () => {
      doc.addPage();
      currentPage++;
      yPosition = 40;
      addPageFooter();
    };

    // Helper function to add page footer
  // Helper function to add page footer
    const addPageFooter = () => {
      doc.setFontSize(8);
      doc.setTextColor(128, 128, 128);
      doc.text('Tcards Digital Business Cards - Confidential Analytics Report', margin, pageHeight - 20);
      doc.text(`Page ${currentPage} of ${totalPages}`, pageWidth - 60, pageHeight - 20);
      doc.text(`Generated: ${new Date().toLocaleDateString()}`, pageWidth - 60, pageHeight - 10);
    };

    // Helper function to check if we need a new page
  // Helper function to check if we need a new page
  // Add Overview section header
    const checkPageBreak = (requiredSpace: number) => {
      if (yPosition + requiredSpace > pageHeight - 40) {
        addNewPage();
      }
    };

    // Helper function to add section header
    const addSectionHeader = (title: string, color: number[]) => {
      checkPageBreak(60);
      doc.setFillColor(color[0], color[1], color[2]);
      doc.rect(margin, yPosition, pageWidth - 2 * margin, 25, 'F');
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(255, 255, 255);
      doc.text(title, margin + 10, yPosition + 16);
      yPosition += 35;
    };

    // TITLE PAGE
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(59, 130, 246);
    doc.text('TCARDS ANALYTICS REPORT', pageWidth / 2, 60, { align: 'center' });
    
    doc.setFontSize(16);
    doc.setFont('helvetica', 'normal');
    doc.text('Comprehensive Digital Business Card Analytics', pageWidth / 2, 80, { align: 'center' });
    
    doc.setFontSize(12);
    doc.setTextColor(75, 85, 99);
    doc.text(`Period: ${getTimeFilterLabel(timeFilter)}`, pageWidth / 2, 100, { align: 'center' });
    doc.text(`Report ID: TCR-${Date.now().toString().slice(-6)}`, pageWidth / 2, 130, { align: 'center' });
    
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(59, 130, 246);
    doc.text('EXECUTIVE SUMMARY', margin + 10, 165);
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(55, 65, 81);
    const safeEngagementRate = analyticsData.userEngagement.totalViews > 0
      ? ((analyticsData.userEngagement.totalClicks / analyticsData.userEngagement.totalViews) * 100)
      : 0;
    const summaryText = [
      `Total Active Users: ${systemStats.activeUsers.toLocaleString()}`,
      `Total Views: ${analyticsData.userEngagement.totalViews.toLocaleString()}`,
      `New Users: ${analyticsData.newUsersStats.totalNewUsers.toLocaleString()} (${analyticsData.newUsersStats.growthRate > 0 ? '+' : ''}${Number.isFinite(analyticsData.newUsersStats.growthRate) ? analyticsData.newUsersStats.growthRate.toFixed(1) : '0.0'}%)`,
      ...(showRevenue ? [`Monthly Revenue: $${((systemStats.silverSubscriptions * 9.99) + (systemStats.goldSubscriptions * 19.99)).toLocaleString()}`] : []),
      `Engagement Rate: ${safeEngagementRate.toFixed(1)}%`
    ];
    
    summaryText.forEach((text, index) => {
      doc.text(text, margin + 10, 180 + (index * 10));
    });
    
    yPosition = 250;
    addPageFooter();
    addNewPage();

    // SECTION 1: OVERVIEW STATISTICS
    addSectionHeader('1. OVERVIEW STATISTICS', [34, 197, 94]);
    
    const overviewStats = [
      ['Metric', 'Value'],
      ['Total Users', systemStats.totalUsers.toLocaleString()],
      ['Active Users', systemStats.activeUsers.toLocaleString()],
      ['Total Views (derived)', analyticsData.userEngagement.totalViews.toLocaleString()],
      ...(totals.viewsServer > 0 ? [["Total Views (server)", totals.viewsServer.toLocaleString()]] as any : []),
      ['Total Clicks (derived)', analyticsData.userEngagement.totalClicks.toLocaleString()],
      ['Total Shares (derived)', analyticsData.userEngagement.totalShares.toLocaleString()],
      ...(totals.sharesServer > 0 ? [["Total Shares (server)", totals.sharesServer.toLocaleString()]] as any : []),
      ['Total Saves (derived)', analyticsData.userEngagement.totalSaves.toLocaleString()],
      ...(totals.savesServer > 0 ? [["Total Saves (server)", totals.savesServer.toLocaleString()]] as any : []),
      ['QR Code Scans (derived)', analyticsData.userEngagement.totalQRScans.toLocaleString()],
      ...(totals.qrScansServer > 0 ? [["QR Code Scans (server)", totals.qrScansServer.toLocaleString()]] as any : []),
      ['Trial Users', systemStats.trialUsers.toLocaleString()],
      ['Silver Subscriptions', systemStats.silverSubscriptions.toLocaleString()],
      ['Gold Subscriptions', systemStats.goldSubscriptions.toLocaleString()]
    ];

    autoTable(doc, {
      startY: yPosition,
      head: [overviewStats[0]],
      body: overviewStats.slice(1),
      theme: 'grid',
      headStyles: { fillColor: [34, 197, 94], textColor: 255, fontSize: 10 },
      styles: { fontSize: 9, cellPadding: 3 },
      columnStyles: { 0: { cellWidth: 80 }, 1: { cellWidth: 60 } }
    });

    yPosition = (doc as any).lastAutoTable.finalY + 20;

    // SECTION 2: NEW USER STATISTICS
    addSectionHeader('2. NEW USER ACQUISITION', [16, 185, 129]);
    
    const newUserStats = [
      ['Metric', 'Value', 'Details'],
      ['Total New Users', analyticsData.newUsersStats.totalNewUsers.toLocaleString(), getTimeFilterLabel(timeFilter)],
      ['New Users Today', analyticsData.newUsersStats.newUsersToday.toLocaleString(), 'Last 24 hours'],
      ['Growth Rate', `${analyticsData.newUsersStats.growthRate > 0 ? '+' : ''}${analyticsData.newUsersStats.growthRate.toFixed(1)}%`, 'vs Previous Period'],
      ['Daily Average', Math.floor(analyticsData.newUsersStats.totalNewUsers / 30).toString(), 'Users per day']
    ];

    autoTable(doc, {
      startY: yPosition,
      head: [newUserStats[0]],
      body: newUserStats.slice(1),
      theme: 'grid',
      headStyles: { fillColor: [16, 185, 129], textColor: 255, fontSize: 10 },
      styles: { fontSize: 9, cellPadding: 3 },
      columnStyles: { 0: { cellWidth: 60 }, 1: { cellWidth: 40 }, 2: { cellWidth: 60 } }
    });

    yPosition = (doc as any).lastAutoTable.finalY + 20;

    // SECTION 3: USER ENGAGEMENT & PERFORMANCE
    addSectionHeader('3. USER ENGAGEMENT & PERFORMANCE', [139, 69, 19]);
    
    const subscriptionData = [
      ['Total New Users', analyticsData.newUsersStats.totalNewUsers.toLocaleString()],
      ['New Users Today', analyticsData.newUsersStats.newUsersToday.toLocaleString()],
      ['Growth Rate (%)', `${analyticsData.newUsersStats.growthRate > 0 ? '+' : ''}${analyticsData.newUsersStats.growthRate.toFixed(1)}%`],
      ['Time Period', getTimeFilterLabel(timeFilter)],
      ['Average Daily New Users', Math.floor(analyticsData.newUsersStats.totalNewUsers / Math.max(1, analyticsData.newUsersStats.dailyNewUsers.length)).toLocaleString()],
      ['Peak Day New Users', Math.max(...analyticsData.newUsersStats.dailyNewUsers.map(d => d.count)).toLocaleString()]
    ];

    autoTable(doc, {
      startY: yPosition,
      head: [subscriptionData[0]],
      body: subscriptionData.slice(1),
      theme: 'striped',
      headStyles: { fillColor: [139, 69, 19], textColor: 255, fontSize: 10 },
      styles: { fontSize: 9, cellPadding: 3 }
    });
    
    yPosition = (doc as any).lastAutoTable.finalY + 20;

    // Check for page break
    if (yPosition > 240) {
      addNewPage();
    }

    // SECTION 4: TOP PERFORMING USERS
    addSectionHeader('4. TOP PERFORMING USERS', [245, 101, 101]);
    
    const topUsersData = analyticsData.mostActiveUsers.slice(0, 5).map((user, index) => [
      `#${index + 1}`,
      user.user.name,
      user.user.position.length > 15 ? user.user.position.substring(0, 15) + '...' : user.user.position,
      user.user.company.length > 15 ? user.user.company.substring(0, 15) + '...' : user.user.company,
      user.activity.toLocaleString(),
      user.user.subscription.type.toUpperCase()
    ]);

    autoTable(doc, {
      startY: yPosition,
      head: [['Rank', 'Name', 'Position', 'Company', 'Score', 'Plan']],
      body: topUsersData,
      theme: 'grid',
      headStyles: { fillColor: [245, 101, 101], textColor: 255, fontSize: 10 },
      styles: { fontSize: 8, cellPadding: 2 },
      columnStyles: { 
        0: { cellWidth: 20 }, 
        1: { cellWidth: 35 }, 
        2: { cellWidth: 35 }, 
        3: { cellWidth: 35 }, 
        4: { cellWidth: 25 }, 
        5: { cellWidth: 20 } 
      }
    });

    yPosition = (doc as any).lastAutoTable.finalY + 20;

    // Check for page break
    if (yPosition > 240) {
      addNewPage();
    }

    // SECTION 5: DEMOGRAPHICS
    addSectionHeader('5. DEMOGRAPHICS', [16, 185, 129]);
    
    // Gender demographics
    const genderData = [
      ['Category', 'Count', 'Percentage'],
      ['Male Users', '678', '54.2%'],
      ['Female Users', '456', '36.5%'],
      ['Other/Unspecified', '116', '9.3%']
    ];

    autoTable(doc, {
      startY: yPosition,
      head: [genderData[0]],
      body: genderData.slice(1),
      theme: 'grid',
      headStyles: { fillColor: [16, 185, 129], textColor: 255, fontSize: 10 },
      styles: { fontSize: 9, cellPadding: 3 },
      columnStyles: { 0: { cellWidth: 60 }, 1: { cellWidth: 40 }, 2: { cellWidth: 60 } }
    });

    yPosition = (doc as any).lastAutoTable.finalY + 15;

    // Age demographics
    const ageData = [
      ['Age Group', 'Count', 'Percentage'],
      ['18-25 Years', '234', '18.7%'],
      ['25-35 Years', '456', '36.5%'],
      ['35-45 Years', '345', '27.6%'],
      ['45-55 Years', '156', '12.5%'],
      ['55+ Years', '59', '4.7%']
    ];

    autoTable(doc, {
      startY: yPosition,
      head: [ageData[0]],
      body: ageData.slice(1),
      theme: 'grid',
      headStyles: { fillColor: [16, 185, 129], textColor: 255, fontSize: 10 },
      styles: { fontSize: 9, cellPadding: 3 },
      columnStyles: { 0: { cellWidth: 60 }, 1: { cellWidth: 40 }, 2: { cellWidth: 60 } }
    });

    yPosition = (doc as any).lastAutoTable.finalY + 20;

    // Check for page break
    if (yPosition > 240) {
      addNewPage();
    }

    // Location distribution (mode-aware: City or Country) — placed within Demographics section
    {
      const useCity = exportLocationMode === 'city';
      const demo = (systemStats as any)?.demographics || {};
      const byCountry = (demo?.locations || {}) as Record<string, number>;
      const byCity = (demo?.locationsByCity || {}) as Record<string, number>;

      // Build a counts map honoring the chosen export mode, with user-derived fallback
      const locCounts: Record<string, number> = {};
      const source = useCity ? byCity : byCountry;
      if (source && Object.keys(source).length > 0) {
        Object.assign(locCounts, source);
      } else {
        // Fallback: derive from users' location field
        users.forEach(u => {
          const raw = String(u.location || '').trim();
          if (!raw) {
            locCounts['Unknown'] = (locCounts['Unknown'] || 0) + 1;
            return;
          }
          if (useCity) {
            if (raw.includes(',')) {
              const parts = raw.split(',').map(s => s.trim()).filter(Boolean);
              const city = parts[0];
              const country = parts[parts.length - 1];
              const key = city && country ? `${city}, ${country}` : raw;
              locCounts[key] = (locCounts[key] || 0) + 1;
            } else {
              // No country part present; keep as-is
              locCounts[raw] = (locCounts[raw] || 0) + 1;
            }
          } else {
            // Country mode: prefer last segment if comma-separated
            const key = raw.includes(',') ? raw.split(',').pop()!.trim() : raw;
            locCounts[key] = (locCounts[key] || 0) + 1;
          }
        });
      }

      const locEntries = Object.entries(locCounts).sort(([, a], [, b]) => b - a).slice(0, 20);
      const totalLoc = locEntries.reduce((s, [, c]) => s + (Number(c) || 0), 0) || 1;

      // Heading
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 0, 0);
      doc.text(`Location Distribution (${useCity ? 'City' : 'Country'})`, 25, yPosition);

      autoTable(doc, {
        startY: yPosition + 5,
        head: [['Location', 'Count', 'Percentage']],
        body: locEntries.map(([label, count]) => [
          label,
          String(count),
          `${(((Number(count) || 0) / totalLoc) * 100).toFixed(1)}%`
        ]),
        theme: 'grid',
        headStyles: { fillColor: [16, 185, 129], textColor: 255, fontSize: 10 },
        styles: { fontSize: 9, cellPadding: 3 },
      });

      yPosition = (doc as any).lastAutoTable.finalY + 20;

      if (yPosition > 240) {
        addNewPage();
      }
    }

    // 6. TRAFFIC SOURCES & PLATFORM ANALYTICS
    yPosition = (doc as any).lastAutoTable.finalY + 15;
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(220, 38, 127);
    doc.text('6. TRAFFIC SOURCES & PLATFORM ANALYTICS', 20, yPosition);
    
    // Traffic Sources from Social Media
    yPosition += 10;
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('Social Media Traffic Sources', 25, yPosition);
    
    const realSocialCounts = pick(
      (systemStats as any)?.traffic?.social,
      (systemStats as any)?.traffic?.socialMedia,
      (systemStats as any)?.socialTraffic,
      (systemStats as any)?.socialMedia
    );
    const socialBreakdownForPdf = realSocialCounts ? objectToBreakdown(realSocialCounts) : buildTrafficBreakdown(analyticsData.userEngagement.totalViews, [
      { label: 'LinkedIn', weight: 0.182 },
      { label: 'Twitter', weight: 0.124 },
      { label: 'Instagram', weight: 0.081 },
      { label: 'Facebook', weight: 0.063 },
      { label: 'Other Social', weight: 0.042 }
    ]);
    const socialTrafficData = socialBreakdownForPdf.map(i => [i.label, i.visits.toLocaleString(), i.percentage]);

    autoTable(doc, {
      startY: yPosition + 5,
      head: [['Platform', 'Visits', 'Traffic Share']],
      body: socialTrafficData,
      theme: 'striped',
      headStyles: { fillColor: [220, 38, 127], textColor: 255 },
      styles: { fontSize: 10 }
    });

    // Other Traffic Sources
    yPosition = (doc as any).lastAutoTable.finalY + 10;
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Other Traffic Sources', 25, yPosition);
    
    const realOtherCounts = pick(
      (systemStats as any)?.traffic?.other,
      (systemStats as any)?.otherTraffic,
      (systemStats as any)?.traffic?.sources
    );
    const otherBreakdownForPdf = realOtherCounts ? objectToBreakdown(realOtherCounts) : buildTrafficBreakdown(analyticsData.userEngagement.totalViews, [
      { label: 'Direct Access', weight: 0.352 },
      { label: 'Search Engines', weight: 0.151 },
      { label: 'Email Campaigns', weight: 0.093 },
      { label: 'Referral Links', weight: 0.072 },
      { label: 'QR Code Scans', weight: 0.050 },
    ]);
    const otherTrafficData = otherBreakdownForPdf.map(i => [i.label, i.visits.toLocaleString(), i.percentage]);

    autoTable(doc, {
      startY: yPosition + 5,
      head: [['Source', 'Visits', 'Traffic Share']],
      body: otherTrafficData,
      theme: 'striped',
      headStyles: { fillColor: [220, 38, 127], textColor: 255 },
      styles: { fontSize: 10 }
    });

    // Device Usage Statistics
    yPosition = (doc as any).lastAutoTable.finalY + 10;
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Device Usage Distribution', 25, yPosition);
    
    const deviceData = [
      ['Mobile', '60.5%'],
      ['Desktop', '31.1%'],
      ['Tablet', '8.4%']
    ];

    autoTable(doc, {
      startY: yPosition + 5,
      head: [['Device Type', 'Usage Percentage']],
      body: deviceData,
      theme: 'striped',
      headStyles: { fillColor: [220, 38, 127], textColor: 255 },
      styles: { fontSize: 10 }
    });

    // 7. ADVERTISEMENT PERFORMANCE ANALYSIS
    yPosition = (doc as any).lastAutoTable.finalY + 15;
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(220, 38, 127);
    doc.text('7. ADVERTISEMENT PERFORMANCE ANALYSIS', 20, yPosition);
    
    // Ad Performance Overview
    yPosition += 10;
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('Advertisement Performance Overview', 25, yPosition);
    
    // Build ad performance rows; include revenue only when allowed
    const adViews = totals.adViewsServer > 0 ? totals.adViewsServer : Math.floor(analyticsData.userEngagement.totalViews * 0.15);
    const adClicks = totals.adClicksServer > 0 ? totals.adClicksServer : Math.floor(analyticsData.userEngagement.totalClicks * 0.08);
    const ctr = adViews > 0 && adClicks > 0 ? `${((adClicks / adViews) * 100).toFixed(2)}%` : '2.1%';

    const adPerformanceData: Array<[string, string]> = [
      ['Total Ad Views', adViews.toLocaleString()],
      ['Total Ad Clicks', adClicks.toLocaleString()],
      ['Click-Through Rate (CTR)', ctr],
      ['Average Cost Per Click', '$0.45'],
      ['Conversion Rate', '3.2%']
    ];

    if (showRevenue) {
      // Insert revenue after CTR
      adPerformanceData.splice(3, 0, ['Total Ad Revenue', `$${(analyticsData.userEngagement.totalClicks * 0.08 * 0.45).toFixed(0).toLocaleString()}`]);
    }

    autoTable(doc, {
      startY: yPosition + 5,
      head: [['Metric', 'Value']],
      body: adPerformanceData,
      theme: 'striped',
      headStyles: { fillColor: [220, 38, 127], textColor: 255 },
      styles: { fontSize: 10 }
    });

  // Best Performing Ads (dynamic from systemStats.bestAds if present)
    yPosition = (doc as any).lastAutoTable.finalY + 10;
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Top Performing Advertisements', 25, yPosition);
    
    const bestAdsList: Array<{ title: string; clicks: number; views: number; ctr: string; revenue?: string }> = (() => {
      const serverAds: any[] = Array.isArray((systemStats as any)?.bestAds) ? (systemStats as any).bestAds : [];
      if (serverAds.length) {
        return serverAds.map(a => {
          const title = String(a.title || 'Ad');
          const clicks = Number(a.clicks || 0);
          const views = Number(a.views || 0);
          const ctrNum = views > 0 && clicks > 0 ? (clicks / views) * 100 : 0;
          const revenueEst = showRevenue ? `$${(clicks * 0.45).toFixed(2)}` : undefined;
          return { title, clicks, views, ctr: `${ctrNum.toFixed(2)}%`, revenue: revenueEst };
        });
      }
      // Fallback static examples
      return [
        { title: 'Premium Business Tools', clicks: 234, views: 15420, ctr: '1.52%', revenue: '$105.30' },
        { title: 'Marketing Automation', clicks: 156, views: 8900, ctr: '1.75%', revenue: '$70.20' },
        { title: 'Professional Services', clicks: 89, views: 6200, ctr: '1.44%', revenue: '$40.05' }
      ];
    })();

    const bestAdsData = bestAdsList.map(a => [a.title, String(a.clicks), a.views.toLocaleString(), a.ctr, a.revenue || '']);

  const bestAdsHead = showRevenue ? ['Ad Title', 'Clicks', 'Views', 'CTR', 'Revenue'] : ['Ad Title', 'Clicks', 'Views', 'CTR'];
  const bestAdsBody = showRevenue ? bestAdsData : bestAdsData.map(row => row.slice(0, 4));

    autoTable(doc, {
      startY: yPosition + 5,
      head: [bestAdsHead],
      body: bestAdsBody,
      theme: 'striped',
      headStyles: { fillColor: [220, 38, 127], textColor: 255 },
      styles: { fontSize: 10 }
    });

    // 8. SUBSCRIPTION ANALYSIS
    yPosition = (doc as any).lastAutoTable.finalY + 15;
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(220, 38, 127);
    doc.text('8. SUBSCRIPTION ANALYSIS', 20, yPosition);
    
    const subscriptionAnalysisData = [
      ['Trial Users', systemStats.trialUsers.toString(), `${((systemStats.trialUsers / systemStats.totalUsers) * 100).toFixed(1)}%`],
      ['Silver Subscriptions', systemStats.silverSubscriptions.toString(), `${((systemStats.silverSubscriptions / systemStats.totalUsers) * 100).toFixed(1)}%`],
      ['Gold Subscriptions', systemStats.goldSubscriptions.toString(), `${((systemStats.goldSubscriptions / systemStats.totalUsers) * 100).toFixed(1)}%`]
    ];

    autoTable(doc, {
      startY: yPosition + 10,
      head: [['Subscription Type', 'Count', 'Percentage']],
      body: subscriptionAnalysisData,
      theme: 'striped',
      headStyles: { fillColor: [220, 38, 127], textColor: 255 },
      styles: { fontSize: 10 }
    });

    // 9. DEMOGRAPHICS ANALYSIS
    yPosition = (doc as any).lastAutoTable.finalY + 15;
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(220, 38, 127);
    doc.text('9. DEMOGRAPHICS ANALYSIS', 25, yPosition + 2);
    
    // Gender Distribution
    yPosition += 15;
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('Gender Distribution', 25, yPosition);
    
    const genderDemographics = [
      ['Male', '678', '54.2%'],
      ['Female', '456', '36.5%'],
      ['Other/Unspecified', '116', '9.3%']
    ];

    autoTable(doc, {
      startY: yPosition + 5,
      head: [['Gender', 'Count', 'Percentage']],
      body: genderDemographics,
      theme: 'striped',
      headStyles: { fillColor: [220, 38, 127], textColor: 255 },
      styles: { fontSize: 10 }
    });

    // Age Distribution
    yPosition = (doc as any).lastAutoTable.finalY + 10;
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Age Distribution', 25, yPosition);
    
    const ageDemographics = [
      ['18-25 Years', '234', '18.7%'],
      ['25-35 Years', '456', '36.5%'],
      ['35-45 Years', '345', '27.6%'],
      ['45-55 Years', '156', '12.5%'],
      ['55+ Years', '59', '4.7%']
    ];

    autoTable(doc, {
      startY: yPosition + 5,
      head: [['Age Group', 'Count', 'Percentage']],
      body: ageDemographics,
      theme: 'striped',
      headStyles: { fillColor: [220, 38, 127], textColor: 255 },
      styles: { fontSize: 10 }
    });

    // 10. KEY PERFORMANCE INDICATORS
    yPosition = (doc as any).lastAutoTable.finalY + 15;
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(220, 38, 127);
    doc.text('10. KEY PERFORMANCE INDICATORS', 20, yPosition);
    
    // Calculate KPIs
    const engagementRate = ((analyticsData.userEngagement.totalClicks / analyticsData.userEngagement.totalViews) * 100);
    const conversionRate = ((systemStats.silverSubscriptions + systemStats.goldSubscriptions) / systemStats.totalUsers) * 100;
    const returningVisitorRate = ((analyticsData.userEngagement.returningVisitors / analyticsData.userEngagement.uniqueVisitors) * 100);
    const avgSessionDuration = 4.2; // Mock average session duration in minutes
    const qrAdoptionRate = ((analyticsData.userEngagement.totalQRScans / analyticsData.userEngagement.totalViews) * 100);
    const adClickThroughRate = 2.1; // Mock CTR for ads
    
    const kpiData = [
      ['User Growth Rate', `+${((systemStats.newUsersToday / systemStats.totalUsers) * 100 * 30).toFixed(1)}%`, 'Positive Growth'],
      ['Engagement Rate', `${engagementRate.toFixed(1)}%`, engagementRate > 5 ? 'Excellent' : engagementRate > 3 ? 'Good' : 'Needs Improvement'],
      ['Conversion Rate (Trial to Paid)', `${conversionRate.toFixed(1)}%`, conversionRate > 3 ? 'Above Average' : 'Average'],
      ['Returning Visitor Rate', `${returningVisitorRate.toFixed(1)}%`, returningVisitorRate > 25 ? 'Excellent' : 'Good'],
      ['Average Session Duration', `${avgSessionDuration} minutes`, avgSessionDuration > 3 ? 'Good' : 'Needs Improvement'],
      ['QR Code Adoption Rate', `${qrAdoptionRate.toFixed(1)}%`, qrAdoptionRate > 2 ? 'Good' : 'Low'],
      ['Ad Click-Through Rate', `${adClickThroughRate}%`, adClickThroughRate > 2 ? 'Excellent' : 'Good']
    ];

    autoTable(doc, {
      startY: yPosition + 10,
      head: [['KPI', 'Value', 'Status']],
      body: kpiData,
      theme: 'striped',
      headStyles: { fillColor: [220, 38, 127], textColor: 255 },
      styles: { fontSize: 10 }
    });

    // 11. HOME PAGE STATS
    yPosition = (doc as any).lastAutoTable.finalY + 15;
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(26, 86, 219);
    doc.text('11. HOME PAGE STATS', 20, yPosition);

    // 11.1 Overview KPIs
    yPosition += 10;
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('Overview', 25, yPosition);

    const homeOverviewRows: Array<[string, string]> = [
      ['Total Home Page Views', Number(homeStats.totalViews || 0).toLocaleString()],
      ['Contact Clicks', Number((homeStats as any).contactClicks || 0).toLocaleString()],
      ['Owner Sign In Clicks', Number((homeStats as any).ownerSignIn || 0).toLocaleString()],
      ['Owner Sign Up Clicks', Number((homeStats as any).ownerSignUp || 0).toLocaleString()],
    ];
    autoTable(doc, {
      startY: yPosition + 5,
      head: [['Metric', 'Value']],
      body: homeOverviewRows,
      theme: 'striped',
      headStyles: { fillColor: [26, 86, 219], textColor: 255 },
      styles: { fontSize: 10 }
    });

    // 11.2 Device Usage
    yPosition = (doc as any).lastAutoTable.finalY + 10;
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Device Usage', 25, yPosition);
    autoTable(doc, {
      startY: yPosition + 5,
      head: [['Device', 'Share']],
      body: (homeStats.deviceEntries || []).map((e: any) => [e.label, String(e.percentage || '-')]),
      theme: 'striped',
      headStyles: { fillColor: [26, 86, 219], textColor: 255 },
      styles: { fontSize: 10 }
    });

    // 11.3 Platform (OS)
    yPosition = (doc as any).lastAutoTable.finalY + 10;
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Platform (OS)', 25, yPosition);
    autoTable(doc, {
      startY: yPosition + 5,
      head: [['Platform', 'Share']],
      body: (homeStats.platformEntries || []).map((e: any) => [e.label, String(e.percentage || '-')]),
      theme: 'striped',
      headStyles: { fillColor: [26, 86, 219], textColor: 255 },
      styles: { fontSize: 10 }
    });

    // 11.4 Social Platforms
    yPosition = (doc as any).lastAutoTable.finalY + 10;
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Social Platforms', 25, yPosition);
    autoTable(doc, {
      startY: yPosition + 5,
      head: [['Platform', 'Share']],
      body: (homeStats.socialEntries || []).map((e: any) => [e.label, String(e.percentage || '-')]),
      theme: 'striped',
      headStyles: { fillColor: [26, 86, 219], textColor: 255 },
      styles: { fontSize: 10 }
    });

    // 11.5 Other Sources
    yPosition = (doc as any).lastAutoTable.finalY + 10;
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Other Sources', 25, yPosition);
    autoTable(doc, {
      startY: yPosition + 5,
      head: [['Source', 'Share']],
      body: (homeStats.otherEntries || []).map((e: any) => [e.label, String(e.percentage || '-')]),
      theme: 'striped',
      headStyles: { fillColor: [26, 86, 219], textColor: 255 },
      styles: { fontSize: 10 }
    });

    // 11.6 Subscription Plan Clicks
    yPosition = (doc as any).lastAutoTable.finalY + 10;
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Subscription Plan Clicks', 25, yPosition);
    const planRows = (homeStats.perPlan || []).map((e: any) => [e.label, Number(e.visits || 0).toLocaleString(), String(e.percentage || '-')]);
    autoTable(doc, {
      startY: yPosition + 5,
      head: [['Plan', 'Clicks', 'Share']],
      body: planRows,
      theme: 'striped',
      headStyles: { fillColor: [26, 86, 219], textColor: 255 },
      styles: { fontSize: 10 }
    });

  // 12. STRATEGIC RECOMMENDATIONS
  // Start on a new page for clarity
  doc.addPage();
  yPosition = 20;
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(220, 38, 127);
    doc.text('12. STRATEGIC RECOMMENDATIONS', 20, yPosition);
    
    yPosition += 10;
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
  doc.text('Key Insights & Strategic Recommendations:', 25, yPosition);
    
    yPosition += 15;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
    
    (recommendations && recommendations.length > 0 ? recommendations : []).forEach((item, index) => {
      if (yPosition > 250) {
        doc.addPage();
        yPosition = 20;
      }
      
      doc.setFont('helvetica', 'bold');
      const sev = item.severity ? ` [${String(item.severity).toUpperCase()}]` : '';
      doc.text(`${index + 1}. ${item.category}${sev}:`, 25, yPosition);
      yPosition += 8;
      
      doc.setFont('helvetica', 'normal');
      const insightLines = doc.splitTextToSize(`Insight: ${item.insight}`, 160);
      doc.text(insightLines, 30, yPosition);
      yPosition += insightLines.length * 5 + 3;
      
      const recommendationLines = doc.splitTextToSize(`Recommendation: ${item.recommendation}`, 160);
      doc.text(recommendationLines, 30, yPosition);
      yPosition += recommendationLines.length * 5 + 8;
    });

    // Add page numbers
    // doc.internal.getNumberOfPages may not be declared in types; guard access
    const pageCount = (doc && (doc as any).internal && typeof (doc as any).internal.getNumberOfPages === 'function')
      ? (doc as any).internal.getNumberOfPages()
      : ((doc as any).internal?.pages || []).length || 1;
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(10);
      doc.setTextColor(128, 128, 128);
      doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.width - 30, doc.internal.pageSize.height - 10);
    }

    // Save the PDF
    doc.save(`comprehensive-analytics-report-${timeFilter}-${Date.now()}.pdf`);
  };

  const exportToCSV = () => {
    const csvData: Array<any[]> = [];
    csvData.push(['TCARDS DIGITAL BUSINESS CARDS - COMPREHENSIVE ANALYTICS DATA']);
    csvData.push(['Generated:', new Date().toLocaleDateString()]);
    csvData.push(['Period:', getTimeFilterLabel(timeFilter)]);
    csvData.push(['Report ID:', `TCR-${Date.now().toString().slice(-6)}`]);
    csvData.push(['']);
    csvData.push(['EXECUTIVE SUMMARY']);
    csvData.push(['Total Users', systemStats.totalUsers.toString()]);
    csvData.push(['Active Cards', systemStats.activeUsers.toString()]);
    csvData.push(['Total Views', analyticsData.userEngagement.totalViews.toString()]);
    csvData.push(['Engagement Rate (%)', ((analyticsData.userEngagement.totalClicks / analyticsData.userEngagement.totalViews) * 100).toFixed(1)]);
    csvData.push(['New Users', analyticsData.newUsersStats.totalNewUsers.toString()]);
    csvData.push(['Growth Rate (%)', analyticsData.newUsersStats.growthRate.toFixed(1)]);
    csvData.push(['']);
    csvData.push(['Advertisement Performance']);
  const csvAdViews = totals.adViewsServer > 0 ? totals.adViewsServer : Math.floor(analyticsData.userEngagement.totalViews * 0.15);
  const csvAdClicks = totals.adClicksServer > 0 ? totals.adClicksServer : Math.floor(analyticsData.userEngagement.totalClicks * 0.08);
  csvData.push(['Ad Views', String(csvAdViews)]);
  csvData.push(['Ad Clicks', String(csvAdClicks)]);
    csvData.push(['Ad Click-Through Rate', '2.1%']);
    if (showRevenue) csvData.push(['Ad Revenue', `$${(analyticsData.userEngagement.totalClicks * 0.08 * 0.45).toFixed(0)}`]);
    csvData.push(['']);
    csvData.push(['Traffic Sources']);
    csvData.push(['LinkedIn Traffic', Math.floor(analyticsData.userEngagement.totalViews * 0.18).toString()]);
    csvData.push(['Twitter Traffic', Math.floor(analyticsData.userEngagement.totalViews * 0.12).toString()]);
    csvData.push(['Instagram Traffic', Math.floor(analyticsData.userEngagement.totalViews * 0.08).toString()]);
    csvData.push(['Facebook Traffic', Math.floor(analyticsData.userEngagement.totalViews * 0.06).toString()]);
    csvData.push(['Direct Access', Math.floor(analyticsData.userEngagement.totalViews * 0.35).toString()]);
    csvData.push(['Search Engines', Math.floor(analyticsData.userEngagement.totalViews * 0.15).toString()]);
    csvData.push(['']);
    csvData.push(['SOCIAL MEDIA DISTRIBUTION']);
    csvData.push(...Object.entries(analyticsData.socialMediaDistribution).map(([platform, count]) => [platform, count.toString()]));
    csvData.push(['']);
    csvData.push(['DEVICE DISTRIBUTION']);
    csvData.push(...Object.entries(analyticsData.deviceDistribution).map(([device, views]) => [device, views.toString()]));
    csvData.push(['']);
    csvData.push(['TRAFFIC SOURCES']);
    if (analyticsData.trafficSources) {
      csvData.push(...Object.entries(analyticsData.trafficSources).map(([source, views]) => [source, String(views)]));
    }

    csvData.push(['']);
    csvData.push(['GEOGRAPHIC DISTRIBUTION']);
    if (analyticsData.geographicDistribution) {
      csvData.push(...Object.entries(analyticsData.geographicDistribution).map(([region, count]) => [region, String(count)]));
    }

    csvData.push(['']);
    csvData.push(['TRAFFIC SOURCES BREAKDOWN']);
    csvData.push(['Direct Traffic', analyticsData.trafficSourceDistribution['Direct'].toString()]);
    csvData.push(['Social Media Traffic', analyticsData.trafficSourceDistribution['Social Media'].toString()]);
    csvData.push(['Search Engine Traffic', analyticsData.trafficSourceDistribution['Search Engines'].toString()]);
    csvData.push(['Email Traffic', analyticsData.trafficSourceDistribution['Email Marketing'].toString()]);
    csvData.push(['Referral Traffic', analyticsData.trafficSourceDistribution['Referrals'].toString()]);

    csvData.push(['']);
    csvData.push(['SOCIAL MEDIA BREAKDOWN']);
    csvData.push(['LinkedIn', analyticsData.socialMediaDistribution['LinkedIn'].toString()]);
    csvData.push(['Twitter', analyticsData.socialMediaDistribution['Twitter'].toString()]);
    csvData.push(['Facebook', analyticsData.socialMediaDistribution['Facebook'].toString()]);
    csvData.push(['Instagram', analyticsData.socialMediaDistribution['Instagram'].toString()]);
    csvData.push(['WhatsApp', analyticsData.socialMediaDistribution['WhatsApp'].toString()]);

    csvData.push(['']);
    csvData.push(['SEARCH ENGINE BREAKDOWN']);
    csvData.push(['Google', analyticsData.searchEngineDistribution['Google'].toString()]);
    csvData.push(['Bing', analyticsData.searchEngineDistribution['Bing'].toString()]);
    csvData.push(['Yahoo', analyticsData.searchEngineDistribution['Yahoo'].toString()]);
    csvData.push(['DuckDuckGo', analyticsData.searchEngineDistribution['DuckDuckGo'].toString()]);
    csvData.push(['Baidu', analyticsData.searchEngineDistribution['Baidu'].toString()]);

    csvData.push(['']);
    csvData.push(['USER ENGAGEMENT']);
    csvData.push(['Total Clicks', analyticsData.userEngagement.totalClicks.toString()]);
    csvData.push(['Total Saves', analyticsData.userEngagement.totalSaves.toString()]);
    csvData.push(['Total Shares', analyticsData.userEngagement.totalShares.toString()]);
    csvData.push(['QR Scans', analyticsData.userEngagement.totalQRScans.toString()]);
    csvData.push(['Unique Visitors', analyticsData.userEngagement.uniqueVisitors.toString()]);
    csvData.push(['Returning Visitors', analyticsData.userEngagement.returningVisitors.toString()]);

    csvData.push(['']);
    csvData.push(['DEMOGRAPHICS - GENDER']);
    csvData.push(['Male', analyticsData.genderDistribution['Male'].toString()]);
    csvData.push(['Female', analyticsData.genderDistribution['Female'].toString()]);
    csvData.push(['Other', analyticsData.genderDistribution['Other'].toString()]);

    csvData.push(['']);
    csvData.push(['DEMOGRAPHICS - AGE']);
    csvData.push(['18-25', analyticsData.ageDistribution['18-25'].toString()]);
    csvData.push(['25-35', analyticsData.ageDistribution['25-35'].toString()]);
    csvData.push(['35-45', analyticsData.ageDistribution['35-45'].toString()]);
    csvData.push(['45-55', analyticsData.ageDistribution['45-55'].toString()]);
    csvData.push(['55+', analyticsData.ageDistribution['55+'].toString()]);

    csvData.push(['']);
    const useCity = exportLocationMode === 'city';
    csvData.push([`DEMOGRAPHICS - LOCATION (${useCity ? 'City' : 'Country'})`]);
    const locEntries = (Array.isArray(locationDistribution)
      ? locationDistribution
      : Object.entries(locationDistribution as any)
    ).slice(0, 50);
    for (const [label, count] of locEntries) {
      csvData.push([label, String(count)]);
    }

    csvData.push(['']);
    csvData.push(['SUBSCRIPTION DATA']);
    csvData.push(['Trial Users', systemStats.trialUsers.toString()]);
    csvData.push(['Silver Subscriptions', systemStats.silverSubscriptions.toString()]);
    csvData.push(['Gold Subscriptions', systemStats.goldSubscriptions.toString()]);
  // HOME PAGE STATS
  csvData.push(['']);
  csvData.push(['HOME PAGE STATS']);
  csvData.push(['Total Home Page Views', String(homeStats.totalViews || 0)]);
  csvData.push(['Contact Clicks', String((homeStats as any).contactClicks || 0)]);
  csvData.push(['Owner Sign In Clicks', String((homeStats as any).ownerSignIn || 0)]);
  csvData.push(['Owner Sign Up Clicks', String((homeStats as any).ownerSignUp || 0)]);
  csvData.push(['']);
  csvData.push(['HOME - DEVICE USAGE']);
  for (const e of (homeStats.deviceEntries || [])) csvData.push([e.label, String(e.percentage || '-')]);
  csvData.push(['']);
  csvData.push(['HOME - PLATFORM (OS)']);
  for (const e of (homeStats.platformEntries || [])) csvData.push([e.label, String(e.percentage || '-')]);
  csvData.push(['']);
  csvData.push(['HOME - SOCIAL PLATFORMS']);
  for (const e of (homeStats.socialEntries || [])) csvData.push([e.label, String(e.percentage || '-')]);
  csvData.push(['']);
  csvData.push(['HOME - OTHER SOURCES']);
  for (const e of (homeStats.otherEntries || [])) csvData.push([e.label, String(e.percentage || '-')]);
  csvData.push(['']);
  csvData.push(['HOME - SUBSCRIPTION PLAN CLICKS']);
  csvData.push(['Plan', 'Clicks', 'Share']);
  for (const e of (homeStats.perPlan || [])) csvData.push([e.label, String(e.visits || 0), String(e.percentage || '-')]);
  // Strategic Recommendations (Generated) — begin on a fresh block
  csvData.push(['']);
  csvData.push(['------------------------------------------------------------']);
  csvData.push(['PAGE BREAK - STRATEGIC RECOMMENDATIONS']);
  csvData.push(['------------------------------------------------------------']);
  csvData.push(['']);
  csvData.push(['STRATEGIC RECOMMENDATIONS']);
  csvData.push(['#', 'Category', 'Severity', 'Insight', 'Recommendation']);
  (recommendations || []).forEach((r, i) => {
    csvData.push([
      String(i + 1),
      r.category,
      String(r.severity || ''),
      r.insight.replace(/\n/g, ' '),
      r.recommendation.replace(/\n/g, ' ')
    ]);
  });
    
    const topUsersData = [
      [''],
      ['TOP PERFORMING USERS'],
      ['Rank', 'Name', 'Company', 'Activity Score', 'Views', 'Subscription'],
      ...analyticsData.mostActiveUsers.slice(0, 10).map((user, index) => [
        `#${index + 1}`,
        user.user.name,
        user.user.company,
        user.activity.toString(),
        user.views.toString(),
        user.user.subscription.type.toUpperCase()
      ])
    ];
    
    const csvContent = csvData.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `comprehensive-analytics-data-${timeFilter}-${Date.now()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const getTimeFilterLabel = (filter: TimeFilter): string => {
    const labels = {
      '24h': 'Last 24 Hours',
      '3d': 'Last 3 Days',
      '7d': 'Last 7 Days',
      '1m': 'Last Month',
      '3m': 'Last 3 Months',
      '6m': 'Last 6 Months',
      '12m': 'Last 12 Months'
    };
    return labels[filter];
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Advanced Analytics</h2>
          <p className="text-gray-600">Comprehensive insights and performance metrics</p>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Time Filter */}
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-gray-500" />
            <select
              value={timeFilter}
              onChange={(e) => setTimeFilter(e.target.value as TimeFilter)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="24h">Last 24 Hours</option>
              <option value="3d">Last 3 Days</option>
              <option value="7d">Last 7 Days</option>
              <option value="1m">Last Month</option>
              <option value="3m">Last 3 Months</option>
              <option value="6m">Last 6 Months</option>
              <option value="12m">Last 12 Months</option>
            </select>
          </div>
          
          {/* Export Buttons + Export Location Mode */}
          <div className="flex gap-2 items-center">
            <div className="flex items-center gap-2 mr-2 text-sm">
              <span className="text-gray-600">Export locations by</span>
              <select
                value={exportLocationMode}
                onChange={(e) => setExportLocationMode(e.target.value as LocationMode)}
                className="border border-gray-300 rounded-lg px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                aria-label="Export locations by"
              >
                <option value="country">Country</option>
                <option value="city">City</option>
              </select>
            </div>
            <button
              onClick={exportToCSV}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
            >
              <Download className="h-4 w-4" />
              CSV
            </button>
            <button
              onClick={exportToPDF}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
            >
              <Download className="h-4 w-4" />
              PDF
            </button>
            <button
              onClick={() => setShowHomeStats(true)}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm"
              title="View Home Page Stats"
            >
              <HomeIcon className="h-4 w-4" />
              Home Stats
            </button>
          </div>
        </div>
      </div>

      {/* Home Stats Modal */}
      {showHomeStats && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowHomeStats(false)} />
          <div className="relative bg-white rounded-xl shadow-2xl border w-[95vw] max-w-5xl max-h-[85vh] overflow-auto">
            <div className="sticky top-0 z-10 flex items-center justify-between px-5 py-3 border-b bg-white/80 backdrop-blur">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 flex items-center gap-2"><HomeIcon className="h-5 w-5 text-indigo-600" /> Home Stats</h3>
                <p className="text-sm text-gray-600">Homepage engagement and traffic overview</p>
              </div>
              <button onClick={() => setShowHomeStats(false)} className="p-2 rounded hover:bg-gray-100" aria-label="Close Home Stats"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-5 space-y-6">
              {/* Top KPI cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="p-5 rounded-lg bg-gradient-to-r from-blue-500 to-indigo-600 text-white">
                  <div className="text-blue-100 text-sm">Total Home Page Views</div>
                  <div className="text-3xl font-bold mt-1">{Number(homeStats.totalViews || 0).toLocaleString()}</div>
                </div>
                <div className="p-5 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-600 text-white">
                  <div className="text-emerald-100 text-sm">Contact Button Clicks</div>
                  <div className="text-3xl font-bold mt-1">{Number(homeStats.contactClicks || 0).toLocaleString()}</div>
                </div>
                <div className="p-5 rounded-lg bg-gradient-to-r from-purple-500 to-fuchsia-600 text-white">
                  <div className="text-purple-100 text-sm">Owner Sign In Clicks</div>
                  <div className="text-3xl font-bold mt-1">{Number(homeStats.ownerSignIn || 0).toLocaleString()}</div>
                </div>
                <div className="p-5 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 text-white">
                  <div className="text-amber-100 text-sm">Owner Sign Up Clicks</div>
                  <div className="text-3xl font-bold mt-1">{Number(homeStats.ownerSignUp || 0).toLocaleString()}</div>
                </div>
              </div>

              {/* Traffic breakdowns */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Device usage */}
                <div className="border rounded-lg p-4 bg-white">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-gray-900 flex items-center gap-2"><Smartphone className="h-4 w-4 text-gray-600" /> Device Usage</h4>
                    <span className="text-xs text-gray-500">Based on homepage sessions</span>
                  </div>
                  <ul className="space-y-3">
                    {homeStats.deviceEntries.map((e:any) => (
                      <li key={e.label} className="grid grid-cols-5 gap-3 items-center">
                        <div className="col-span-1 text-sm text-gray-700">{e.label}</div>
                        <div className="col-span-3 h-2 rounded-full bg-gray-100">
                          <div className="h-2 rounded-full bg-indigo-600" style={{ width: e.percentage }} />
                        </div>
                        <div className="col-span-1 text-right text-xs text-gray-600">{e.percentage}</div>
                      </li>
                    ))}
                  </ul>
                </div>
                {/* Platform */}
                <div className="border rounded-lg p-4 bg-white">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-gray-900 flex items-center gap-2"><Globe className="h-4 w-4 text-gray-600" /> Platform (OS)</h4>
                  </div>
                  <ul className="space-y-3">
                    {homeStats.platformEntries.map((e:any) => (
                      <li key={e.label} className="grid grid-cols-5 gap-3 items-center">
                        <div className="col-span-1 text-sm text-gray-700">{e.label}</div>
                        <div className="col-span-3 h-2 rounded-full bg-gray-100">
                          <div className="h-2 rounded-full bg-blue-600" style={{ width: e.percentage }} />
                        </div>
                        <div className="col-span-1 text-right text-xs text-gray-600">{e.percentage}</div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Sources breakdowns */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="border rounded-lg p-4 bg-white">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-gray-900 flex items-center gap-2"><Share2 className="h-4 w-4 text-gray-600" /> Social Platforms</h4>
                  </div>
                  <ul className="space-y-3">
                    {homeStats.socialEntries.map((e:any) => (
                      <li key={e.label} className="grid grid-cols-5 gap-3 items-center">
                        <div className="col-span-1 text-sm text-gray-700">{e.label}</div>
                        <div className="col-span-3 h-2 rounded-full bg-gray-100">
                          <div className="h-2 rounded-full bg-purple-600" style={{ width: e.percentage }} />
                        </div>
                        <div className="col-span-1 text-right text-xs text-gray-600">{e.percentage}</div>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="border rounded-lg p-4 bg-white">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-gray-900 flex items-center gap-2"><MousePointer className="h-4 w-4 text-gray-600" /> Other Platforms</h4>
                  </div>
                  <ul className="space-y-3">
                    {homeStats.otherEntries.map((e:any) => (
                      <li key={e.label} className="grid grid-cols-5 gap-3 items-center">
                        <div className="col-span-1 text-sm text-gray-700">{e.label}</div>
                        <div className="col-span-3 h-2 rounded-full bg-gray-100">
                          <div className="h-2 rounded-full bg-emerald-600" style={{ width: e.percentage }} />
                        </div>
                        <div className="col-span-1 text-right text-xs text-gray-600">{e.percentage}</div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Subscription plan clicks */}
              <div className="border rounded-lg p-4 bg-white">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="font-semibold text-gray-900">Subscription Plan Clicks</h4>
                    <p className="text-xs text-gray-500">Total clicks on pricing CTAs and per-plan distribution</p>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-gray-600">Total</div>
                    <div className="text-2xl font-bold text-gray-900">{Number(homeStats.totalPlanClicks || 0).toLocaleString()}</div>
                  </div>
                </div>
                <ul className="space-y-3">
                  {homeStats.perPlan.map((e:any) => (
                    <li key={e.label} className="grid grid-cols-5 gap-3 items-center">
                      <div className="col-span-1 text-sm text-gray-700">{e.label}</div>
                      <div className="col-span-3 h-2 rounded-full bg-gray-100">
                        <div className="h-2 rounded-full bg-amber-600" style={{ width: e.percentage }} />
                      </div>
                      <div className="col-span-1 text-right text-xs text-gray-600">{e.percentage}</div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Chart Navigation */}
      <div className="flex flex-wrap gap-2">
        {[
          { key: 'overview', label: 'Overview', icon: BarChart3 },
          { key: 'demographics', label: 'Demographics', icon: Users },
          { key: 'engagement', label: 'Engagement', icon: Activity },
          { key: 'trends', label: 'Trends', icon: TrendingUp }
        ].map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setActiveChart(key as any)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeChart === key
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Icon className="h-4 w-4" />
            {label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeChart === 'overview' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Guidance banner when no server nor derived data yet */}
          {overviewDerived.showGuidance && (
            <div className="md:col-span-2 lg:col-span-3 p-4 rounded-lg border bg-amber-50 text-amber-900">
              <div className="flex items-start gap-3">
                <div className="mt-0.5">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.993.883L9 8v4a1 1 0 001.993.117L11 12V8a1 1 0 00-1-1zm0 8a1 1 0 100 2 1 1 0 000-2z" clipRule="evenodd" /></svg>
                </div>
                <div>
                  <p className="font-semibold">Waiting for first live interactions</p>
                  <p className="text-sm mt-1">Open a public card (not in admin/studio/auth routes), view the card, click contact/email/social buttons, share, save, and interact with ads. The panel auto-refreshes and will show non‑zero totals once events are recorded.</p>
                </div>
              </div>
            </div>
          )}
          {/* Key Metrics Cards */}
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-6 rounded-lg text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100">Total Views</p>
                <p className="text-2xl font-bold">{analyticsData.userEngagement.totalViews.toLocaleString()}</p>
                {totals.viewsServer > 0 && (
                  <p className="text-xs text-blue-200">server: {totals.viewsServer.toLocaleString()}</p>
                )}
                <p className="text-xs text-blue-200">in {getTimeFilterLabel(timeFilter).toLowerCase()}</p>
              </div>
              <Eye className="w-8 h-8 text-blue-200" />
            </div>
          </div>
          

          <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 rounded-lg text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100">Total Clicks</p>
                <p className="text-2xl font-bold">{overviewDerived.totalClicksEffective.toLocaleString()}</p>
                <p className="text-xs text-green-200">in {getTimeFilterLabel(timeFilter).toLowerCase()}</p>
              </div>
              <MousePointer className="w-8 h-8 text-green-200" />
            </div>
          </div>

          <div className="bg-gradient-to-r from-purple-500 to-purple-600 p-6 rounded-lg text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100">Total Shares</p>
                <p className="text-2xl font-bold">{overviewDerived.totalSharesEffective.toLocaleString()}</p>
                {(totals.sharesServer || 0) > 0 && (
                  <p className="text-xs text-purple-200">server: {Number(totals.sharesServer || 0).toLocaleString()}</p>
                )}
                <p className="text-xs text-purple-200">in {getTimeFilterLabel(timeFilter).toLowerCase()}</p>
              </div>
              <Share2 className="w-8 h-8 text-purple-200" />
            </div>
          </div>

          {/* Ad Performance Summary */}
          <div className="bg-gradient-to-r from-orange-500 to-orange-600 p-6 rounded-lg text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-100">Ad Views</p>
                <p className="text-2xl font-bold">{overviewDerived.adViewsEffective.toLocaleString()}</p>
                <p className="text-xs text-orange-200">total impressions</p>
              </div>
              <Target className="w-8 h-8 text-orange-200" />
            </div>
          </div>

          <div className="bg-gradient-to-r from-pink-500 to-pink-600 p-6 rounded-lg text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-pink-100">Ad Clicks</p>
                <p className="text-2xl font-bold">{overviewDerived.adClicksEffective.toLocaleString()}</p>
                <p className="text-xs text-pink-200">click-through rate: {(() => { const v = overviewDerived.adViewsEffective; const c = overviewDerived.adClicksEffective; return v>0 && c>0 ? `${((c / v) * 100).toFixed(2)}%` : '2.1%'; })()}</p>
              </div>
              <MousePointer className="w-8 h-8 text-pink-200" />
            </div>
          </div>

          <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 p-6 rounded-lg text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-indigo-100">Ad Revenue</p>
                <p className="text-2xl font-bold">${(() => {
                  // Only estimate revenue when ad monetization is explicitly enabled.
                  const monetization = (systemStats as any)?.monetization || {};
                  const monetized = Boolean((systemStats as any)?.adRevenueEnabled || (systemStats as any)?.adsMonetized || monetization?.ads);
                  if (!monetized) return '0';
                  const model = String(monetization?.model || (systemStats as any)?.adBillingMode || '').toUpperCase();
                  const share = Number.isFinite(Number(monetization?.share)) ? Number(monetization?.share) : (Number((systemStats as any)?.adRevenueShare) || 1);
                  const adViews = Number((overviewDerived as any)?.adViewsEffective ?? totals.adViewsServer ?? 0);
                  const adClicks = Number((overviewDerived as any)?.adClicksEffective ?? totals.adClicksServer ?? 0);
                  const cpc = Number((systemStats as any)?.adCPC ?? monetization?.cpc ?? 0);
                  const cpv = Number((systemStats as any)?.adCPV ?? monetization?.cpv ?? 0);
                  const cpm = Number((systemStats as any)?.adCPM ?? monetization?.cpm ?? 0);
                  let est = 0;
                  if (model === 'CPC' && cpc > 0) est = adClicks * cpc * share;
                  else if (model === 'CPV' && cpv > 0) est = adViews * cpv * share;
                  else if (model === 'CPM' && cpm > 0) est = Math.floor(adViews / 1000) * cpm * share;
                  else est = 0; // Unknown model or no rates configured
                  return Math.round(est).toLocaleString();
                })()}</p>
                <p className="text-xs text-indigo-200">{(() => {
                  const monetization = (systemStats as any)?.monetization || {};
                  const monetized = Boolean((systemStats as any)?.adRevenueEnabled || (systemStats as any)?.adsMonetized || monetization?.ads);
                  if (!monetized) return 'monetization disabled';
                  const model = String(monetization?.model || (systemStats as any)?.adBillingMode || '').toUpperCase();
                  const hasRate = (r: any) => Number.isFinite(Number(r)) && Number(r) > 0;
                  const ok = (model === 'CPC' && hasRate((systemStats as any)?.adCPC ?? monetization?.cpc))
                    || (model === 'CPV' && hasRate((systemStats as any)?.adCPV ?? monetization?.cpv))
                    || (model === 'CPM' && hasRate((systemStats as any)?.adCPM ?? monetization?.cpm));
                  return ok ? 'estimated earnings' : 'rate not set';
                })()}</p>
              </div>
              <DollarSign className="w-8 h-8 text-indigo-200" />
            </div>
          </div>

          {/* Ad Redirects (New Card) */}
          <div className="bg-gradient-to-r from-teal-500 to-teal-600 p-6 rounded-lg text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-teal-100">Ad Redirects</p>
                <p className="text-2xl font-bold">{overviewDerived.adRedirectsEffective.toLocaleString()}</p>
                <p className="text-xs text-teal-200">successful external landings</p>
              </div>
              <Globe className="w-8 h-8 text-teal-200" />
            </div>
          </div>

          {/* Top Performing Users */}
          <div className="md:col-span-2 lg:col-span-3 bg-white p-6 rounded-lg border">
            <h3 className="text-lg font-semibold mb-4">Top Performing Users</h3>
            <div className="space-y-3">
              {analyticsData.mostActiveUsers.slice(0, 5).map((user, index) => (
                <div key={user.user.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${
                      index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-gray-400' : index === 2 ? 'bg-orange-500' : 'bg-blue-500'
                    }`}>
                      {index + 1}
                    </div>
                    <div>
                      <p className="font-medium">{user.user.name}</p>
                      <p className="text-sm text-gray-600">{user.user.position}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold">{user.activity}</p>
                    <p className="text-sm text-gray-600">Activity Score</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Traffic Sources */}
          <div className="md:col-span-2 lg:col-span-3 bg-white p-6 rounded-lg border">
            <h3 className="text-lg font-semibold mb-4">Traffic Sources</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium mb-3">Social Media Traffic</h4>
                <ul className="space-y-3">
                  {(realSocialCounts ? objectToBreakdownWithTotal(realSocialCounts, overallTotalViews) : buildTrafficBreakdown(overallTotalViews, [
                    { label: 'LinkedIn', weight: 0.182 },
                    { label: 'Twitter', weight: 0.124 },
                    { label: 'Instagram', weight: 0.081 },
                    { label: 'Facebook', weight: 0.063 },
                    { label: 'Other Social', weight: 0.042 },
                  ])).map((source, index) => (
                    <li key={index} className="grid grid-cols-5 gap-3 items-center">
                      <div className="col-span-2 text-sm text-gray-700">{source.label}</div>
                      <div className="col-span-2 h-2 rounded-full bg-gray-100">
                        <div className="h-2 rounded-full bg-blue-500" style={{ width: (source as any).percentageLabel ? `${(source as any).percentage}%` : source.percentage }} />
                      </div>
                      <div className="col-span-1 text-right text-xs text-gray-600">
                        <div className="font-medium">{source.visits.toLocaleString()} visits</div>
                        <div className="text-gray-500">{(source as any).percentageLabel ?? source.percentage} of total traffic</div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="font-medium mb-3">Other Traffic Sources</h4>
                <ul className="space-y-3">
                  {(realOtherCounts ? objectToBreakdownWithTotal(realOtherCounts, overallTotalViews) : buildTrafficBreakdown(overallTotalViews, [
                    { label: 'Direct Access', weight: 0.352 },
                    { label: 'Search Engines', weight: 0.151 },
                    { label: 'Email Campaigns', weight: 0.093 },
                    { label: 'Referral Links', weight: 0.072 },
                    { label: 'QR Code Scans', weight: 0.050 },
                  ])).map((source, index) => (
                    <li key={index} className="grid grid-cols-5 gap-3 items-center">
                      <div className="col-span-2 text-sm text-gray-700">{source.label}</div>
                      <div className="col-span-2 h-2 rounded-full bg-gray-100">
                        <div className="h-2 rounded-full bg-green-500" style={{ width: (source as any).percentageLabel ? `${(source as any).percentage}%` : source.percentage }} />
                      </div>
                      <div className="col-span-1 text-right text-xs text-gray-600">
                        <div className="font-medium">{source.visits.toLocaleString()} visits</div>
                        <div className="text-gray-500">{(source as any).percentageLabel ?? source.percentage} of total traffic</div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Best Performing Ads */}
          <div className="md:col-span-2 lg:col-span-3 bg-white p-6 rounded-lg border">
            <h3 className="text-lg font-semibold mb-4">Best Performing Ads</h3>
            <div className="space-y-3">
              {(() => {
                const serverAds: any[] = Array.isArray((systemStats as any)?.bestAds) ? (systemStats as any).bestAds : [];
                const items = serverAds.length
                  ? serverAds
                  : [
                      { title: 'Premium Business Tools', clicks: 234, views: 15420, ctr: '1.52%', revenue: '$105.30' },
                      { title: 'Marketing Automation', clicks: 156, views: 8900, ctr: '1.75%', revenue: '$70.20' },
                      { title: 'Professional Services', clicks: 89, views: 6200, ctr: '1.44%', revenue: '$40.05' }
                    ];
                return items;
              })().map((ad: any, index: number) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${
                      index === 0 ? 'bg-green-500' : index === 1 ? 'bg-blue-500' : 'bg-purple-500'
                    }`}>
                      {index + 1}
                    </div>
                    <div>
                      <p className="font-medium">{String(ad.title || 'Ad')}</p>
                      <p className="text-sm text-gray-600">CTR: {ad.ctr ?? (() => { const v = Number(ad.views || 0), c = Number(ad.clicks || 0); return v>0 && c>0 ? `${((c / v) * 100).toFixed(2)}%` : '—'; })()}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold">{Number(ad.clicks || 0).toLocaleString()} clicks</p>
                    <p className="text-sm text-gray-600">{Number(ad.views || 0).toLocaleString()} views</p>
                    {'revenue' in ad ? (
                      <p className="text-sm text-green-600 font-medium">{ad.revenue}</p>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Strategic Recommendations */}
          <div className="md:col-span-2 lg:col-span-3 bg-white p-6 rounded-lg border">
            <h3 className="text-lg font-semibold mb-2">Strategic Recommendations</h3>
            <p className="text-sm text-gray-600 mb-4">Automatically generated from your current metrics</p>
            <div className="space-y-4">
              {(recommendations && recommendations.length > 0 ? recommendations : []).map((r, idx) => (
                <div key={idx} className="p-4 rounded-lg border bg-gray-50">
                  <div className="flex items-center justify-between mb-1">
                    <div className="font-semibold text-gray-900">{r.category}</div>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      r.severity === 'high' ? 'bg-red-100 text-red-700' : r.severity === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'
                    }`}>
                      {String(r.severity || 'info').toUpperCase()}
                    </span>
                  </div>
                  <div className="text-sm text-gray-700"><span className="font-medium">Insight:</span> {r.insight}</div>
                  <div className="text-sm text-gray-700 mt-1"><span className="font-medium">Recommendation:</span> {r.recommendation}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Overview Stats Cards */}
      {activeChart === 'overview' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Views</p>
                <p className="text-2xl font-bold text-gray-900">
                  {analyticsData.userEngagement.totalViews.toLocaleString()}
                </p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <Eye className="h-6 w-6 text-blue-600" />
              </div>
            </div>
            <div className="mt-4 flex items-center text-sm">
              <span className="text-green-600 font-medium">
                +{(() => { const base = Number(systemStats.totalViews) || 1; const curr = analyticsData.userEngagement.totalViews; return (((curr / base) - 1) * 100).toFixed(1); })()}%
              </span>
              <span className="text-gray-600 ml-2">vs previous period</span>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">New Users</p>
                <p className="text-2xl font-bold text-gray-900">
                  {analyticsData.newUsersStats.totalNewUsers.toLocaleString()}
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-lg">
                <UserCheck className="h-6 w-6 text-green-600" />
              </div>
            </div>
            <div className="mt-4 flex items-center text-sm">
              <span className={`font-medium ${analyticsData.newUsersStats.growthRate >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {analyticsData.newUsersStats.growthRate >= 0 ? '+' : ''}{analyticsData.newUsersStats.growthRate.toFixed(1)}%
              </span>
              <span className="text-gray-600 ml-2">growth rate</span>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Clicks</p>
                <p className="text-2xl font-bold text-gray-900">
                  {analyticsData.userEngagement.totalClicks.toLocaleString()}
                </p>
              </div>
              <div className="p-3 bg-purple-100 rounded-lg">
                <Activity className="h-6 w-6 text-purple-600" />
              </div>
            </div>
            <div className="mt-4 flex items-center text-sm">
              <span className="text-blue-600 font-medium">
                {(() => { const v = analyticsData.userEngagement.totalViews; return (v > 0 ? ((analyticsData.userEngagement.totalClicks / v) * 100) : 0).toFixed(1); })()}%
              </span>
              <span className="text-gray-600 ml-2">engagement rate</span>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">QR Scans</p>
                <p className="text-2xl font-bold text-gray-900">
                  {analyticsData.userEngagement.totalQRScans.toLocaleString()}
                </p>
              </div>
              <div className="p-3 bg-orange-100 rounded-lg">
                <QrCode className="h-6 w-6 text-orange-600" />
              </div>
            </div>
            <div className="mt-4 flex items-center text-sm">
              <span className="text-orange-600 font-medium">
                {(() => { const v = analyticsData.userEngagement.totalViews; return (v > 0 ? ((analyticsData.userEngagement.totalQRScans / v) * 100) : 0).toFixed(1); })()}%
              </span>
              <span className="text-gray-600 ml-2">of total views</span>
            </div>
          </div>
        </div>
      )}

      {/* Top Performing Users - Moved to Overview */}
      {activeChart === 'overview' && (
        <div className="bg-white p-6 rounded-lg border">
          <h3 className="text-lg font-semibold mb-4 text-gray-900">Top Performing Users</h3>
          <div className="space-y-3">
            {analyticsData.mostActiveUsers.slice(0, 5).map((user, index) => (
              <div key={user.user.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-600 rounded-full font-semibold text-sm">
                    {index + 1}
                  </div>
                  <img 
                    src={user.user.avatar} 
                    alt={user.user.name}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                  <div>
                    <p className="font-medium text-gray-900">{user.user.name}</p>
                    <p className="text-sm text-gray-600">{user.user.position}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-gray-900">{user.activity}</p>
                  <p className="text-xs text-gray-500">Activity Score</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Demographics Charts */}
      {activeChart === 'demographics' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Gender Distribution */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Gender Distribution</h3>
            <div className="space-y-4">
              {Object.entries(analyticsData.genderDistribution).map(([gender, count]) => {
                const total = Object.values(analyticsData.genderDistribution).reduce((sum, val) => sum + val, 0);
                const percentage = ((count / total) * 100).toFixed(1);
                return (
                  <div key={gender} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-4 h-4 rounded-full ${
                        gender === 'Male' ? 'bg-blue-500' : 
                        gender === 'Female' ? 'bg-pink-500' : 'bg-gray-500'
                      }`} />
                      <span className="text-sm font-medium text-gray-700">{gender}</span>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-semibold text-gray-900">{count.toLocaleString()}</div>
                      <div className="text-xs text-gray-500">{percentage}%</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Age Distribution */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Age Distribution</h3>
            <div className="space-y-4">
              {Object.entries(analyticsData.ageDistribution).map(([age, count]) => {
                const total = Object.values(analyticsData.ageDistribution).reduce((sum, val) => sum + val, 0);
                const percentage = ((count / total) * 100).toFixed(1);
                return (
                  <div key={age} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-4 h-4 rounded-full bg-green-500" />
                      <span className="text-sm font-medium text-gray-700">{age}</span>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-semibold text-gray-900">{count.toLocaleString()}</div>
                      <div className="text-xs text-gray-500">{percentage}%</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Location Distribution */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              Location Distribution
            </h3>
            <div className="mb-3 flex items-center gap-3 text-sm">
              <span className="text-gray-600">View by:</span>
              <button className={`px-2 py-1 rounded border ${locationMode==='country'?'bg-blue-50 text-blue-700 border-blue-300':'bg-white text-gray-700'}`} onClick={() => setLocationMode('country')}>Country</button>
              <button className={`px-2 py-1 rounded border ${locationMode==='city'?'bg-blue-50 text-blue-700 border-blue-300':'bg-white text-gray-700'}`} onClick={() => setLocationMode('city')}>City</button>
            </div>
            <div className="space-y-4">
              {(() => {
                const total = locationDistribution.reduce((sum, [, v]) => sum + (Number(v) || 0), 0) || 1;
                return locationDistribution.map(([loc, cnt]) => {
                  const countNum = Number(cnt) || 0;
                  const percentage = ((countNum / total) * 100).toFixed(1);
                  return (
                    <div key={loc} className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-4 h-4 rounded-full bg-purple-500" />
                        <span className="text-sm font-medium text-gray-700">{loc}</span>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-semibold text-gray-900">{countNum.toLocaleString()}</div>
                        <div className="text-xs text-gray-500">{percentage}%</div>
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          </div>

          {/* Device Distribution */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Smartphone className="h-5 w-5" />
              Device Distribution
            </h3>
            <div className="space-y-4">
              {Object.entries(analyticsData.deviceDistribution).map(([device, count]) => {
                const total = Object.values(analyticsData.deviceDistribution).reduce((sum, val) => sum + val, 0);
                const percentage = ((count / total) * 100).toFixed(1);
                return (
                  <div key={device} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-4 h-4 rounded-full ${
                        device === 'Mobile' ? 'bg-blue-500' : 
                        device === 'Desktop' ? 'bg-gray-500' : 'bg-orange-500'
                      }`} />
                      <span className="text-sm font-medium text-gray-700">{device}</span>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-semibold text-gray-900">{count.toLocaleString()}</div>
                      <div className="text-xs text-gray-500">{percentage}%</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Engagement Analytics */}
      {activeChart === 'engagement' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Performing Users */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 lg:col-span-2">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Performing Users</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Rank</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">User</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Company</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Views</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Clicks</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Shares</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">QR Scans</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Score</th>
                  </tr>
                </thead>
                <tbody>
                  {analyticsData.mostActiveUsers.slice(0, 10).map((user, index) => (
                    <tr key={user.user.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white ${
                            index === 0 ? 'bg-yellow-500' : 
                            index === 1 ? 'bg-gray-400' : 
                            index === 2 ? 'bg-orange-600' : 'bg-blue-500'
                          }`}>
                            {index + 1}
                          </span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <img
                            src={user.user.avatar}
                            alt={user.user.name}
                            className="w-8 h-8 rounded-full"
                          />
                          <div>
                            <div className="font-medium text-gray-900">{user.user.name}</div>
                            <div className="text-sm text-gray-500">{user.user.position}</div>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-700">{user.user.company}</td>
                      <td className="py-3 px-4 text-sm font-medium text-gray-900">{user.views.toLocaleString()}</td>
                      <td className="py-3 px-4 text-sm font-medium text-gray-900">{user.clicks.toLocaleString()}</td>
                      <td className="py-3 px-4 text-sm font-medium text-gray-900">{user.shares.toLocaleString()}</td>
                      <td className="py-3 px-4 text-sm font-medium text-gray-900">{user.qrScans.toLocaleString()}</td>
                      <td className="py-3 px-4">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {user.activity.toLocaleString()}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* User Engagement Metrics */}
          <div className="bg-white p-6 rounded-lg border">
            <h3 className="text-lg font-semibold mb-4 text-gray-900">User Engagement</h3>
            {/* Engagement Metrics */}
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">{analyticsData.userEngagement.totalClicks.toLocaleString()}</div>
                <div className="text-sm text-gray-600">Total Clicks</div>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-600">{analyticsData.userEngagement.totalShares.toLocaleString()}</div>
                <div className="text-sm text-gray-600">Total Shares</div>
              </div>
              <div className="text-center p-4 bg-purple-50 rounded-lg">
                <div className="text-2xl font-bold text-purple-600">{analyticsData.userEngagement.totalSaves.toLocaleString()}</div>
                <div className="text-sm text-gray-600">Total Saves</div>
              </div>
              <div className="text-center p-4 bg-orange-50 rounded-lg">
                <div className="text-2xl font-bold text-orange-600">{analyticsData.userEngagement.totalQRScans.toLocaleString()}</div>
                <div className="text-sm text-gray-600">QR Scans</div>
              </div>
            </div>
            {/* Event-backed visitor metrics */}
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div className="text-center p-4 bg-sky-50 rounded-lg">
                <div className="text-2xl font-bold text-sky-600">{Number((systemStats as any)?.uniqueVisitors7d || 0).toLocaleString()}</div>
                <div className="text-sm text-gray-600">Unique Visitors (7d)</div>
              </div>
              <div className="text-center p-4 bg-indigo-50 rounded-lg">
                <div className="text-2xl font-bold text-indigo-600">{Number((systemStats as any)?.uniqueVisitors30d || 0).toLocaleString()}</div>
                <div className="text-sm text-gray-600">Unique Visitors (30d)</div>
              </div>
              <div className="text-center p-4 bg-emerald-50 rounded-lg">
                <div className="text-2xl font-bold text-emerald-600">{Number((systemStats as any)?.returningVisitors30d || 0).toLocaleString()}</div>
                <div className="text-sm text-gray-600">Returning Visitors (30d)</div>
              </div>
              <div className="text-center p-4 bg-amber-50 rounded-lg">
                <div className="text-2xl font-bold text-amber-600">{(() => {
                  const secs = Number((systemStats as any)?.averageSessionTime || 0);
                  if (!Number.isFinite(secs) || secs <= 0) return '—';
                  const mins = Math.floor(secs / 60);
                  const rem = Math.floor(secs % 60);
                  return `${mins}m ${rem}s`;
                })()}</div>
                <div className="text-sm text-gray-600">Avg Session Time</div>
              </div>
            </div>
          </div>

          {/* Extended Key Totals (Server-first) */}
          <div className="bg-white p-6 rounded-lg border">
            <h3 className="text-lg font-semibold mb-4 text-gray-900">Key Totals (Server-first)</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-gray-600">Card Clicks</div>
                <div className="font-bold">{(totals.cardClicksServer || aggregateFromUsers('contactClicks')).toLocaleString()}</div>
              </div>
              <div>
                <div className="text-gray-600">Email Clicks</div>
                <div className="font-bold">{(totals.emailClicksServer || aggregateFromUsers('messageClicks')).toLocaleString()}</div>
              </div>
              <div>
                <div className="text-gray-600">Share Clicks</div>
                <div className="font-bold">{(totals.shareClicksServer || aggregateFromUsers('shares')).toLocaleString()}</div>
              </div>
              <div>
                <div className="text-gray-600">Call Clicks</div>
                <div className="font-bold">{(totals.callClicksServer || Math.floor(aggregateFromUsers('contactClicks') * 0.4)).toLocaleString()}</div>
              </div>
              <div>
                <div className="text-gray-600">Saves</div>
                <div className="font-bold">{(totals.savesServer || aggregateFromUsers('saves')).toLocaleString()}</div>
              </div>
              <div>
                <div className="text-gray-600">Social Clicks</div>
                <div className="font-bold">{(totals.socialClicksServer || aggregateFromUsers('socialClicks')).toLocaleString()}</div>
              </div>
              <div>
                <div className="text-gray-600">QR Scans</div>
                <div className="font-bold">{(totals.qrScansServer || aggregateFromUsers('qrScans')).toLocaleString()}</div>
              </div>
            </div>
          </div>

          {/* Social Media Distribution (real or mocked) */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Social Media Traffic</h3>
            <div className="space-y-4">
              {(realSocialCounts ? objectToBreakdown(realSocialCounts) : buildTrafficBreakdown(analyticsData.userEngagement.totalViews, [
                { label: 'LinkedIn', weight: 0.182 },
                { label: 'Twitter', weight: 0.124 },
                { label: 'Instagram', weight: 0.081 },
                { label: 'Facebook', weight: 0.063 },
                { label: 'Other Social', weight: 0.042 },
              ])).map((row) => {
                const platform = row.label;
                const count = row.visits;
                const percentage = row.percentage.replace('%','');
                return (
                  <div key={platform} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-4 h-4 rounded-full ${
                        platform === 'LinkedIn' ? 'bg-blue-600' : 
                        platform === 'Twitter' ? 'bg-sky-500' : 
                        platform === 'Facebook' ? 'bg-blue-800' :
                        platform === 'Instagram' ? 'bg-pink-500' : 'bg-green-500'
                      }`} />
                      <span className="text-sm font-medium text-gray-700">{platform}</span>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-semibold text-gray-900">{count.toLocaleString()}</div>
                      <div className="text-xs text-gray-500">{percentage}%</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Traffic Sources (progress bars against overall total) */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Globe className="h-5 w-5" />
              Other Traffic Sources
            </h3>
            <div className="space-y-4">
              {(realOtherCounts ? objectToBreakdownWithTotal(realOtherCounts, overallTotalViews) : buildTrafficBreakdown(overallTotalViews, [
                { label: 'Direct Access', weight: 0.352 },
                { label: 'Search Engines', weight: 0.151 },
                { label: 'Email Campaigns', weight: 0.093 },
                { label: 'Referral Links', weight: 0.072 },
                { label: 'QR Code Scans', weight: 0.050 },
              ])).map((row: any) => (
                <div key={row.label} className="grid grid-cols-5 gap-3 items-center">
                  <div className="col-span-2 text-sm font-medium text-gray-700">{row.label}</div>
                  <div className="col-span-2 h-2 rounded-full bg-gray-100">
                    <div className="h-2 rounded-full bg-green-500" style={{ width: row.percentageLabel ? `${row.percentage}%` : row.percentage }} />
                  </div>
                  <div className="col-span-1 text-right text-xs text-gray-600">
                    <div className="font-semibold text-gray-900">{row.visits.toLocaleString()} visits</div>
                    <div className="text-gray-500">{row.percentageLabel ?? row.percentage} of total traffic</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Trends */}
      {activeChart === 'trends' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* New Users Trend */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 lg:col-span-2">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">New Users Trend</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">{analyticsData.newUsersStats.totalNewUsers.toLocaleString()}</div>
                <div className="text-sm text-gray-600">Total New Users</div>
                <div className="text-xs text-gray-500">{getTimeFilterLabel(timeFilter)}</div>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-600">{analyticsData.newUsersStats.newUsersToday.toLocaleString()}</div>
                <div className="text-sm text-gray-600">New Users Today</div>
                <div className="text-xs text-gray-500">Last 24 hours</div>
              </div>
              <div className="text-center p-4 bg-purple-50 rounded-lg">
                <div className={`text-2xl font-bold ${analyticsData.newUsersStats.growthRate >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {analyticsData.newUsersStats.growthRate >= 0 ? '+' : ''}{analyticsData.newUsersStats.growthRate.toFixed(1)}%
                </div>
                <div className="text-sm text-gray-600">Growth Rate</div>
                <div className="text-xs text-gray-500">vs Previous Period</div>
              </div>
            </div>
            
            {/* Weekly Breakdown Chart */}
            <div className="md:col-span-2 bg-white p-6 rounded-lg border">
              <h3 className="text-lg font-semibold mb-4">Weekly New Users Breakdown</h3>
              <div className="space-y-2">
                <h4 className="font-medium text-gray-700">Daily New Users (Last 7 Days)</h4>
                <div className="space-y-2">
                  {analyticsData.newUsersStats.dailyNewUsers.slice(0, 7).map((day, index) => (
                    <div key={day.date} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded">
                      <span className="text-sm text-gray-600">{day.date}</span>
                      <div className="flex items-center gap-3">
                        <div className="w-32 bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-blue-600 h-2 rounded-full" 
                            style={{ width: `${(day.count / Math.max(...analyticsData.newUsersStats.dailyNewUsers.slice(0, 7).map(d => d.count))) * 100}%` }}
                          />
                        </div>
                        <span className="text-sm font-medium text-gray-900 w-12 text-right">{day.count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Subscription Trends */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Briefcase className="h-5 w-5" />
              Subscription Distribution
            </h3>
            <div className="space-y-4">
              {Object.entries(analyticsData.subscriptionTrends).map(([plan, count]) => {
                const total = Object.values(analyticsData.subscriptionTrends).reduce((sum, val) => sum + val, 0);
                const percentage = ((count / total) * 100).toFixed(1);
                return (
                  <div key={plan} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-4 h-4 rounded-full ${
                        plan === 'Trial' ? 'bg-gray-500' : 
                        plan === 'Silver' ? 'bg-gray-400' : 'bg-yellow-500'
                      }`} />
                      <span className="text-sm font-medium text-gray-700">{plan} Plan</span>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-semibold text-gray-900">{count.toLocaleString()}</div>
                      <div className="text-xs text-gray-500">{percentage}%</div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Revenue Calculation */}
            <div className="mt-6 pt-4 border-t border-gray-200">
              <div className="text-sm text-gray-600 mb-2">Estimated Monthly Revenue</div>
              <div className="text-2xl font-bold text-green-600">
                ${((analyticsData.subscriptionTrends.Silver * 9.99) + (analyticsData.subscriptionTrends.Gold * 19.99)).toLocaleString()}
              </div>
            </div>
          </div>

          {/* Search Engine Distribution */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Search Engine Traffic</h3>
            <div className="space-y-4">
              {Object.entries(analyticsData.searchEngineDistribution).map(([engine, count]) => {
                const total = Object.values(analyticsData.searchEngineDistribution).reduce((sum, val) => sum + val, 0);
                const percentage = ((count / total) * 100).toFixed(1);
                return (
                  <div key={engine} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-4 h-4 rounded-full ${
                        engine === 'Google' ? 'bg-red-500' : 
                        engine === 'Bing' ? 'bg-blue-500' : 
                        engine === 'Yahoo' ? 'bg-purple-500' :
                        engine === 'DuckDuckGo' ? 'bg-orange-500' : 'bg-green-500'
                      }`} />
                      <span className="text-sm font-medium text-gray-700">{engine}</span>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-semibold text-gray-900">{count.toLocaleString()}</div>
                      <div className="text-xs text-gray-500">{percentage}%</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};